import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  LinearProgress,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  Tooltip,
  Switch,
  Checkbox,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Dashboard,
  People,
  Assessment,
  CheckCircle,
  Cancel,
  Save,
  TrendingUp,
  TrendingDown,
  Schedule,
  ExpandMore,
  ExpandLess,
  VerifiedUser,
  Warning,
  Edit,
} from '@mui/icons-material';
import samplesService, { Sample as SampleType, Priority, SampleStatus } from '../../services/samplesService';

interface Sample {
  id: string;
  sample_id: string;
  external_id?: string; // Client reference
  type_echantillon: string;
  statut: string;
  date_reception: string;
  date_prelevament: string;
  nom_projet?: string; // Project name
  site_minier?: string; // Mining site
  client: {
    company_name: string;
  };
  priorite: 'NORMALE' | 'URGENTE' | 'CRITIQUE';
  methodes_demandees?: string[];
  laboratoire_assigne?: string;
  metadonnees?: any;
  date_creation: string;
  date_modification: string;
}

interface SampleWithAnalyst extends Sample {
  analyst_name?: string;
}

interface LabRealTimeStatus {
  total_samples_in_lab: number;
  samples_prep_chimique: number;
  samples_analysis: number;
  samples_validation_pending: number;
  instruments_active: number;
  instruments_total: number;
  current_workload_percent: number;
  estimated_completion_hours: number;
  last_updated: string;
}

interface StaffPerformance {
  user_id: string;
  user_name: string;
  user_role: 'PREPARATION' | 'ANALYST';
  samples_completed_today: number;
  samples_completed_week: number;
  samples_completed_month: number;
  average_completion_time_hours: number;
  quality_score_percent: number;
  reanalysis_rate_percent: number;
  efficiency_trend: 'UP' | 'DOWN' | 'STABLE';
  last_activity: string;
}

interface QCSample {
  id: string;
  batch_id: string;
  qc_type: 'CRM' | 'LCS' | 'METHOD_BLANK' | 'REAGENT_BLANK' | 'DUPLICATE' | 'MATRIX_SPIKE';
  laboratory: string;
  reference_material_id?: string;
  certified_values?: any;
  measured_values: any;
  qc_results: any;
  acceptance_criteria: any;
  analysis_date: string;
  analyzed_by: string;
  instrument_id: string;
  passed: boolean;
  corrective_action?: string;
  notes?: string;
}

// 🔬 QC Correction interfaces
interface QCElementData {
  tq_average: number;
  tq_count: number;
  tq_rsd: number;
  certified_value?: number;
  tq_calculated?: number;      // TQ concentration after dilution formula
  correction_coefficient?: number;
  recovery_percent?: number;   // (TQ_calculated / Certified) × 100
}

interface BatchQCData {
  batchNumber: string;
  qcData: Record<string, QCElementData>;
  certifiedValues: Record<string, { value: number; unit: string }>;
  alerts: any[];
}

const SAASupervisorDashboard: React.FC = () => {
  const theme = useTheme();
  const [currentTab, setCurrentTab] = useState(0);
  const [samples, setSamples] = useState<SampleWithAnalyst[]>([]);
  const [labStatus, setLabStatus] = useState<LabRealTimeStatus | null>(null);
  const [staffPerformance, setStaffPerformance] = useState<StaffPerformance[]>([]);
  const [loading, setLoading] = useState(true);
  const [validationDialog, setValidationDialog] = useState(false);
  const [selectedSample, setSelectedSample] = useState<SampleWithAnalyst | null>(null);
  const [validationDecision, setValidationDecision] = useState<'APPROVE' | 'REJECT' | 'REQUEST_CORRECTIONS'>('APPROVE');
  const [validationNotes, setValidationNotes] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [qcSamples, setQcSamples] = useState<QCSample[]>([]);
  const [expandedBatches, setExpandedBatches] = useState<Set<string>>(new Set());
  
  // 🔬 QC Correction state
  const [batchQCData, setBatchQCData] = useState<BatchQCData | null>(null);
  const [qcLoading, setQcLoading] = useState(false);
  const [correctedValues, setCorrectedValues] = useState<Record<string, Record<string, number>>>({});
  const [showCorrectedValues, setShowCorrectedValues] = useState(false);
  const [editedCertifiedValues, setEditedCertifiedValues] = useState<Record<string, number>>({});
  const [editingCell, setEditingCell] = useState<string | null>(null);
  const [editCellValue, setEditCellValue] = useState<string>('');

  // 📊 QA/QC Reference Table state (BIS selection, blanks, CRM standards)
  interface SAAQAQCStandard {
    customers_ref: string;
    lab_ref: string;
    elements: Record<string, string>;
  }
  const [qaqcStandards, setQaqcStandards] = useState<SAAQAQCStandard[]>([]);
  const [showQaqcForm, setShowQaqcForm] = useState(false);
  const [firstBISSampleId, setFirstBISSampleId] = useState<string | null>(null);
  const [secondBISSampleId, setSecondBISSampleId] = useState<string | null>(null);
  const [thirdBISSampleId, setThirdBISSampleId] = useState<string | null>(null);
  const [fourthBISSampleId, setFourthBISSampleId] = useState<string | null>(null);

  useEffect(() => {
    loadDashboardData();
    const interval = setInterval(loadDashboardData, 10000); // Optimized: 10 seconds for general data
    return () => clearInterval(interval);
  }, []);

  // Add real-time updates for validation list
  useEffect(() => {
    // Poll for validation updates more frequently
    const validationInterval = setInterval(() => {
      if (currentTab === 2) { // Only poll when on validation tab
        loadValidationSamples();
      }
    }, 5000); // Optimized: 5 seconds for critical validation updates

    return () => clearInterval(validationInterval);
  }, [currentTab]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadLabRealTimeStatus(),
        loadStaffPerformance(),
        loadValidationSamples(),
        loadQualityControlData()
      ]);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadLabRealTimeStatus = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch('/api/v1/samples/laboratory-status/SAA', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setLabStatus(data);
      } else {
        setLabStatus({
          total_samples_in_lab: 0,
          samples_prep_chimique: 0,
          samples_analysis: 0,
          samples_validation_pending: 0,
          instruments_active: 0,
          instruments_total: 0,
          current_workload_percent: 0,
          estimated_completion_hours: 0,
          last_updated: new Date().toISOString()
        });
      }
    } catch (error) {
      console.error('Error loading lab status:', error);
    }
  };

  const loadStaffPerformance = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch('/api/v1/samples/staff-performance/SAA', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStaffPerformance(Array.isArray(data) ? data : []);
      } else {
        setStaffPerformance([]);
      }
    } catch (error) {
      console.error('Error loading staff performance:', error);
      setStaffPerformance([]);
    }
  };

  const loadValidationSamples = async () => {
    try {
      console.log('🔄 SAA Supervisor: Loading validation samples...');
      
      // Use samplesService instead of raw fetch to avoid JSON parsing issues
      // First get the total count, then fetch all if reasonable
      const countResponse = await samplesService.getSamples(1, 1, {
        status: [SampleStatus.VALIDATION_SERVICE]
      });
      const totalValidation = countResponse.total || 0;
      
      // Fetch all validation samples (up to 10000 for performance)
      const limit = Math.min(totalValidation, 10000);
      const response = await samplesService.getSamples(1, limit || 200, {
        status: [SampleStatus.VALIDATION_SERVICE]
      });
      
      const allSamples = response.samples || [];
      console.log(`📊 Found ${allSamples.length} total VALIDATION_SERVICE samples`);
      
      // Filter for SAA-related samples - ONLY show samples with actual SAA analysis
      const saaSamples = allSamples.filter((sample: any) => {
        const hasSaaAnalysis = sample.metadonnees?.saa_analysis_form || sample.metadonnees?.saa_analysis_data;
        
        // Regular SAA samples with SAA analysis data
        if (sample.statut === 'VALIDATION_SERVICE' && hasSaaAnalysis && 
            !sample.metadonnees?.multi_lab_workflow) {
          return true;
        }
        
        // Multi-lab samples with SAA elements that completed SAA analysis but not SAA validation
        const isMultiLab = sample.metadonnees?.multi_lab_workflow === true;
        if (isMultiLab && sample.statut === 'VALIDATION_SERVICE') {
          const selectedElements = sample.metadonnees?.selected_elements || [];
          const elementRouting = sample.metadonnees?.element_lab_routing || {};
          // 🔧 FIX: Check if ANY selected element routes to SAA (not just elementRouting values)
          const hasSaaElements = selectedElements.some((element: string) => 
            elementRouting[element] === 'LABORATOIRE_SAA'
          );
          // 🔧 FIX: Also check laboratories_involved as fallback
          const hasSaaInLabs = sample.laboratories_involved?.includes('LABORATOIRE_SAA') || false;
          const saaAnalysisDone = sample.metadonnees?.saa_analysis_completed === true;
          const saaValidationNotDone = sample.metadonnees?.saa_validation_completed !== true;
          return (hasSaaElements || hasSaaInLabs) && saaAnalysisDone && saaValidationNotDone;
        }
        
        return false;
      }) as any[];
      
      console.log(`🔬 Found ${saaSamples.length} SAA validation samples`);
      
      if (saaSamples.length > 0) {
        console.log('✅ SAA samples found:', saaSamples.map(s => ({
          sample_id: s.sample_id,
          batch_number: s.metadonnees?.batch_analysis_summary?.batch_number,
          is_batch_sample: s.metadonnees?.batch_analysis_summary?.is_batch_sample,
          has_saa_analysis: !!s.metadonnees?.saa_analysis_form
        })));
      }
      
      setSamples(saaSamples);
      
    } catch (error) {
      console.error('❌ Error loading validation samples:', error);
      setSamples([]);
    }
  };

  const loadQualityControlData = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      try {
        const qcResponse = await fetch('/api/v1/samples/qc-samples?laboratory=SAA', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (qcResponse.ok) {
          const qcData = await qcResponse.json();
          setQcSamples(Array.isArray(qcData) ? qcData : []);
        } else {
          setQcSamples([]);
        }
      } catch (error) {
        console.log('QC samples API not available');
        setQcSamples([]);
      }
    } catch (error) {
      console.error('Error loading quality control data:', error);
      setQcSamples([]);
    }
  };

  const handleValidateSample = (sample: SampleWithAnalyst) => {
    setSelectedSample(sample);
    setValidationDialog(true);
    setValidationDecision('APPROVE');
    setValidationNotes('');
  };

  const handleCompleteValidation = async () => {
    if (!selectedSample || !validationNotes.trim()) return;

    try {
      const token = localStorage.getItem('access_token');
      const isBatchValidation = (selectedSample as any)?.isBatchValidation;
      const batchSamples = (selectedSample as any)?.batchSamples || [selectedSample];
      
      if (isBatchValidation) {
        // Batch validation - validate all samples in the batch
        console.log(`🔄 Starting batch validation for ${batchSamples.length} samples`);
        
        let successCount = 0;
        let errorCount = 0;
        const errors: string[] = [];
        
        // 🚀 BATCH OPTIMIZATION: Concurrent validation calls instead of sequential
        console.log(`🔄 Batch validating ${batchSamples.length} SAA samples concurrently...`);
        
        const validationPromises = batchSamples.map(sample => 
          fetch(`/api/v1/samples/${sample.id}/validate`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              decision: validationDecision,
              notes: `${validationNotes} [Validation de lot: ${getBatchNumber(selectedSample)}]`,
              validated_by: 'SAA Supervisor',
              laboratory: 'LABORATOIRE_SAA',
              batch_validation: true,
              batch_number: getBatchNumber(selectedSample),
              ...(showQaqcForm && qaqcStandards.length > 0 && {
                qaqc_data: {
                  reference_standards: qaqcStandards.map(s => ({
                    customers_ref: s.customers_ref,
                    lab_ref: s.lab_ref,
                    ...s.elements,
                  })),
                  batch_info: {
                    analyst_name: batchSamples[0]?.metadonnees?.saa_analyst_name || 'N/A',
                    analysis_date: new Date().toISOString(),
                    batch_number: getBatchNumber(selectedSample) || 'N/A',
                    sample_count: batchSamples.length,
                  }
                }
              })
            })
          }).then(response => ({ sample, response }))
          .catch(error => ({ sample, error }))
        );

        // Wait for all validation calls to complete
        const results = await Promise.all(validationPromises);
        
        // Process results
        for (const result of results) {
          const { sample, response, error } = result;
          
          if (error) {
            errorCount++;
            errors.push(`${sample.sample_id}: Erreur de connexion`);
            console.error(`❌ Validation error for ${sample.sample_id}:`, error);
          } else if (response.ok) {
            successCount++;
            console.log(`✅ Sample ${sample.sample_id} validated successfully`);
          } else {
            errorCount++;
            let errorMessage = 'Erreur inconnue';
            
            try {
              const errorData = await response.json();
              errorMessage = errorData.message || `Erreur ${response.status}`;
            } catch {
              errorMessage = `Erreur HTTP ${response.status}`;
            }
            
            errors.push(`${sample.sample_id}: ${errorMessage}`);
            console.error(`❌ Validation failed for ${sample.sample_id}: ${errorMessage}`);
          }
        }
        
        console.log(`✅ Batch validation completed: ${successCount} successful, ${errorCount} errors`);
        
        // Show results
        if (successCount === batchSamples.length) {
          setSnackbarMessage(`✅ Lot ${getBatchNumber(selectedSample)} ${validationDecision === 'APPROVE' ? 'approuvé' : 'rejeté'} avec succès - ${successCount} échantillons ${validationDecision === 'APPROVE' ? 'envoyés en génération de certificat' : 'renvoyés en analyse'}`);
        } else if (successCount > 0) {
          setSnackbarMessage(`⚠️ Validation partielle: ${successCount}/${batchSamples.length} échantillons validés. ${errorCount} erreurs.`);
        } else {
          setSnackbarMessage(`❌ Échec de la validation du lot: ${errors.join(', ')}`);
        }
        
      } else {
        // Individual sample validation
        const response = await fetch(`/api/v1/samples/${selectedSample.id}/validate`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            decision: validationDecision,
            notes: validationNotes,
            validated_by: 'SAA Supervisor',
            laboratory: 'LABORATOIRE_SAA',
            ...(showQaqcForm && qaqcStandards.length > 0 && {
              qaqc_data: {
                reference_standards: qaqcStandards.map(s => ({
                  customers_ref: s.customers_ref,
                  lab_ref: s.lab_ref,
                  ...s.elements,
                })),
                batch_info: {
                  analyst_name: 'N/A',
                  analysis_date: new Date().toISOString(),
                  batch_number: 'INDIVIDUAL',
                  sample_count: 1,
                }
              }
            })
          })
        });

        if (response.ok) {
          const result = await response.json();
          setSnackbarMessage(`Échantillon ${selectedSample.sample_id} ${validationDecision === 'APPROVE' ? 'approuvé et envoyé en génération de certificat' : 'rejeté et renvoyé en analyse'} avec succès`);
          console.log('Sample validation completed:', result);
        } else {
          const errorData = await response.json();
          console.error('Validation failed:', errorData);
          setSnackbarMessage(`Erreur lors de la validation: ${errorData.message || 'Erreur inconnue'}`);
          setSnackbarOpen(true);
          return;
        }
      }
      
      setSnackbarOpen(true);
      setValidationDialog(false);
      setSelectedSample(null);
      setValidationNotes('');
      setValidationDecision('APPROVE');
      
      // Reload the validation samples list to remove the validated samples
      await loadValidationSamples();
      
    } catch (error) {
      console.error('Error validating samples:', error);
      setSnackbarMessage('Erreur lors de la validation - Vérifiez votre connexion');
      setSnackbarOpen(true);
    }
  };

  const getPriorityColor = (priority: 'NORMALE' | 'URGENTE' | 'CRITIQUE') => {
    switch (priority) {
      case 'CRITIQUE': return 'error';
      case 'URGENTE': return 'warning';
      default: return 'primary';
    }
  };

  // Helper functions for batch processing
  const groupSamplesByBatch = (samples: SampleWithAnalyst[]) => {
    const batches: Record<string, SampleWithAnalyst[]> = {};
    const individualSamples: SampleWithAnalyst[] = [];

    samples.forEach(sample => {
      const batchInfo = sample.metadonnees?.batch_info || sample.metadonnees?.batch_analysis_summary;
      if (batchInfo && (batchInfo.is_batch_sample || batchInfo.batch_number)) {
        const batchNumber = batchInfo.batch_number;
        if (!batches[batchNumber]) {
          batches[batchNumber] = [];
        }
        batches[batchNumber].push(sample);
      } else {
        individualSamples.push(sample);
      }
    });

    return { batches, individualSamples };
  };

  const isBatchSample = (sample: SampleWithAnalyst): boolean => {
    return sample.metadonnees?.batch_info?.is_batch_sample === true || 
           sample.metadonnees?.batch_analysis_summary?.batch_number !== undefined;
  };

  const getBatchNumber = (sample: SampleWithAnalyst): string | null => {
    return sample.metadonnees?.batch_info?.batch_number || 
           sample.metadonnees?.batch_analysis_summary?.batch_number || null;
  };

  const getBatchPosition = (sample: SampleWithAnalyst): number => {
    return sample.metadonnees?.batch_info?.sample_position || 1;
  };

  const getTotalSamplesInBatch = (sample: SampleWithAnalyst): number => {
    return sample.metadonnees?.batch_info?.total_samples || 1;
  };

  // 🔬 Load QC data for a batch
  const loadBatchQCData = async (batchNumber: string) => {
    setQcLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      
      // Fetch QC data for this batch
      const qcResponse = await fetch(`/api/v1/saa-qc/batch/${encodeURIComponent(batchNumber)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Fetch certified values
      const certifiedResponse = await fetch('/api/v1/saa-qc/certified-values', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      let qcData: Record<string, QCElementData> = {};
      let certifiedValues: Record<string, { value: number; unit: string }> = {};
      
      if (qcResponse.ok) {
        const data = await qcResponse.json();
        // 🔄 Backend now returns qcSummary at top level
        qcData = data.qcSummary || data.data?.qc_summary || {};
        console.log('🔬 QC data from API:', data, 'Parsed qcData:', qcData);
      } else {
        console.warn('⚠️ QC API response not OK:', qcResponse.status, qcResponse.statusText);
      }
      
      if (certifiedResponse.ok) {
        const data = await certifiedResponse.json();
        // 🔄 Backend now returns certifiedValues at top level
        certifiedValues = data.certifiedValues || data.data || {};
        console.log('🔬 Certified values from API:', certifiedValues);
      } else {
        console.warn('⚠️ Certified values API response not OK:', certifiedResponse.status);
      }
      
      // 🔬 Calculate correction coefficients using proper dilution formula
      // Formula: TQ_calculated = tq_reading × (V/p) × (VD/PD) [÷ 10000 for %]
      // Coefficient = Certified_Value / TQ_calculated
      
      // Default dilution parameters (standard SAA method)
      const V = 200;   // volume ml
      const p = 0.25;  // mass g
      const baseFactor = V / p; // = 800
      
      // Element-specific VD factors (from standard SAA methods)
      // For ppm elements: VD=1, for % elements: VD=100
      const elementVDFactors: Record<string, number> = {
        'Ag': 1, 'Au': 1, 'Cd': 1, 'Co': 1, 'Cu': 1, 'Mn': 1, 'Ni': 1,  // ppm elements
        'Pb': 100, 'Zn': 100, 'Fe': 100,  // % elements (high concentration)
      };
      
      Object.keys(qcData).forEach(element => {
        const elementQC = qcData[element];
        const certified = certifiedValues[element];
        if (certified && elementQC.tq_average > 0) {
          elementQC.certified_value = certified.value;
          
          // Get VD factor for this element (default to 1 if not specified)
          const VD = elementVDFactors[element] || 1;
          const PD = 1;
          
          // Calculate TQ concentration using dilution formula
          let tqCalculated = elementQC.tq_average * baseFactor * (VD / PD);
          
          // For % units, convert ppm to % by dividing by 10000
          if (certified.unit === '%') {
            tqCalculated = tqCalculated / 10000;
          }
          
          // Store calculated values
          elementQC.tq_calculated = Math.round(tqCalculated * 10000) / 10000;
          elementQC.correction_coefficient = Math.round((certified.value / tqCalculated) * 10000) / 10000;
          elementQC.recovery_percent = Math.round((tqCalculated / certified.value) * 100 * 100) / 100;
          
          console.log(`🔬 ${element}: TQ_avg=${elementQC.tq_average}, VD=${VD}, TQ_calc=${tqCalculated.toFixed(4)}, Certified=${certified.value}${certified.unit}, Coeff=${elementQC.correction_coefficient}, Recovery=${elementQC.recovery_percent}%`);
        }
      });
      
      setBatchQCData({
        batchNumber,
        qcData,
        certifiedValues,
        alerts: []
      });
      
      console.log('🔬 Loaded QC data for batch:', batchNumber, qcData);
      
    } catch (error) {
      console.error('Error loading QC data:', error);
      setBatchQCData(null);
    } finally {
      setQcLoading(false);
    }
  };

  // 🔬 Handle certified value edit - recalculates coefficient and recovery
  const handleCertifiedValueChange = (element: string, newValue: number) => {
    if (!batchQCData || !newValue || newValue <= 0) return;
    
    const elementQC = batchQCData.qcData[element];
    if (!elementQC || !elementQC.tq_calculated || elementQC.tq_calculated <= 0) return;
    
    const tqCalculated = elementQC.tq_calculated;
    
    // Calculate new coefficient and recovery with new certified value
    const newCoefficient = Math.round((newValue / tqCalculated) * 10000) / 10000;
    const newRecovery = Math.round((tqCalculated / newValue) * 100 * 100) / 100;
    
    console.log(`🔬 ${element}: Certified value changed to ${newValue}, New Coeff=${newCoefficient}, Recovery=${newRecovery}%`);
    
    // Update edited values state
    setEditedCertifiedValues(prev => ({
      ...prev,
      [element]: newValue
    }));
    
    // 🔥 Create a completely new state object to ensure React re-renders
    const newQCData: Record<string, QCElementData> = {};
    Object.entries(batchQCData.qcData).forEach(([el, qc]) => {
      if (el === element) {
        // Create new object with updated values for this element
        newQCData[el] = {
          ...qc,
          certified_value: newValue,
          correction_coefficient: newCoefficient,
          recovery_percent: newRecovery
        };
      } else {
        // Keep other elements as-is (but still new object reference)
        newQCData[el] = { ...qc };
      }
    });
    
    // Update state with recalculated values - NEW OBJECTS ensure re-render
    setBatchQCData({
      ...batchQCData,
      qcData: newQCData,
      certifiedValues: {
        ...batchQCData.certifiedValues,
        [element]: { 
          value: newValue, 
          unit: batchQCData.certifiedValues[element]?.unit || 'ppm' 
        }
      }
    });
  };

  // 🔬 Apply QC correction to a sample's element
  const applyCorrection = (sampleId: string, element: string, originalValue: number, coefficient: number) => {
    const correctedValue = originalValue * coefficient;
    setCorrectedValues(prev => ({
      ...prev,
      [sampleId]: {
        ...(prev[sampleId] || {}),
        [element]: correctedValue
      }
    }));
    setSnackbarMessage(`✅ Correction appliquée: ${element} pour ${sampleId} (${originalValue.toFixed(4)} → ${correctedValue.toFixed(4)} ppm)`);
    setSnackbarOpen(true);
  };

  // 🔬 Apply correction to all samples for an element - NOW SAVES TO DATABASE!
  const applyElementCorrection = async (element: string, coefficient: number, batchSamples: any[]) => {
    const batchNumber = batchQCData?.batchNumber || getBatchNumber(selectedSample);
    
    if (!batchNumber) {
      setSnackbarMessage(`❌ Erreur: Numéro de lot introuvable`);
      setSnackbarOpen(true);
      return;
    }
    
    try {
      const token = localStorage.getItem('access_token');
      
      // 🔥 CALL BACKEND API TO PERSIST CORRECTIONS TO DATABASE
      const response = await fetch(`/api/v1/saa-qc/batch/${encodeURIComponent(batchNumber)}/apply-correction/element`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          element,
          coefficient
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Erreur ${response.status}`);
      }
      
      const result = await response.json();
      console.log(`✅ Correction saved to database:`, result);
      
      // Also update local state for immediate UI feedback
      const newCorrectedValues = { ...correctedValues };
      let count = 0;
      
      batchSamples.forEach(sample => {
        const analysisData = sample.metadonnees?.saa_analysis_form || sample.metadonnees?.batch_analysis_data;
        if (analysisData?.analysis_readings) {
          const elementReading = analysisData.analysis_readings.find(
            (r: any) => r.element?.toUpperCase() === element.toUpperCase()
          );
          if (elementReading) {
            const originalPpm = elementReading.concentration_ppm || 0;
            if (originalPpm > 0) {
              if (!newCorrectedValues[sample.id]) {
                newCorrectedValues[sample.id] = {};
              }
              newCorrectedValues[sample.id][element] = originalPpm * coefficient;
              count++;
            }
          }
        }
      });
      
      setCorrectedValues(newCorrectedValues);
      setShowCorrectedValues(true); // Auto-enable to show corrected values
      
      setSnackbarMessage(`✅ Correction ${element} enregistrée: ${result.updatedCount || count} échantillons corrigés (coef: ${coefficient.toFixed(4)}) - Sauvegardé en base de données!`);
      setSnackbarOpen(true);
      
    } catch (error: any) {
      console.error('❌ Error applying correction:', error);
      setSnackbarMessage(`❌ Erreur lors de l'application de la correction: ${error.message}`);
      setSnackbarOpen(true);
    }
  };

  const handleValidateBatch = async (batchSamples: SampleWithAnalyst[]) => {
    // For batch validation, we'll set the first sample as selectedSample
    // but we'll also store the entire batch for display
    if (batchSamples.length > 0) {
      setSelectedSample({
        ...batchSamples[0],
        // Add batch context to the sample
        batchSamples: batchSamples,
        isBatchValidation: true
      } as any);
      setValidationDialog(true);
      setValidationDecision('APPROVE');
      setValidationNotes('');
      setCorrectedValues({});
      setShowCorrectedValues(false);

      // Load QC data for this batch
      const batchNumber = batchSamples[0]?.metadonnees?.batch_info?.batch_number ||
                          batchSamples[0]?.metadonnees?.batch_analysis_summary?.batch_number;
      if (batchNumber) {
        await loadBatchQCData(batchNumber);
      }
    }
  };

  // Manually override a concentration value from the supervisor table.
  // Persisted immediately via /saa-qc/sample/:id/edit-concentration so that
  // the certificate generation picks up corrected_ppm + correction_applied.
  const saveConcentrationEdit = async (sampleId: string, element: string, newValueStr: string, unit: string) => {
    const parsed = parseFloat(String(newValueStr).replace(',', '.'));
    if (isNaN(parsed)) {
      setEditingCell(null);
      return;
    }
    const newValuePpm = unit === '%' ? parsed * 10000 : parsed;

    // Optimistic UI update so the corrected column reflects the edit instantly
    const previousForSample = correctedValues[sampleId] ? { ...correctedValues[sampleId] } : undefined;
    setCorrectedValues(prev => ({
      ...prev,
      [sampleId]: { ...(prev[sampleId] || {}), [element]: newValuePpm }
    }));
    setEditingCell(null);

    try {
      const response = await fetch(`/api/v1/saa-qc/sample/${sampleId}/edit-concentration`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
        body: JSON.stringify({ element, new_value_ppm: newValuePpm, unit }),
      });
      if (response.ok) {
        setSnackbarMessage(`Concentration ${element} mise à jour avec succès`);
        setSnackbarOpen(true);
      } else {
        // Roll back the optimistic update on failure
        setCorrectedValues(prev => {
          const next = { ...prev };
          if (previousForSample === undefined) {
            delete next[sampleId];
          } else {
            next[sampleId] = previousForSample;
          }
          return next;
        });
        let msg = 'Impossible de sauvegarder';
        try { const err = await response.json(); msg = err?.message || msg; } catch {}
        setSnackbarMessage(`Erreur: ${msg}`);
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error saving concentration edit:', error);
      setCorrectedValues(prev => {
        const next = { ...prev };
        if (previousForSample === undefined) {
          delete next[sampleId];
        } else {
          next[sampleId] = previousForSample;
        }
        return next;
      });
      setSnackbarMessage('Erreur lors de la sauvegarde');
      setSnackbarOpen(true);
    }
  };


  const toggleBatchExpansion = (batchNumber: string) => {
    const newExpandedBatches = new Set(expandedBatches);
    if (newExpandedBatches.has(batchNumber)) {
      newExpandedBatches.delete(batchNumber);
    } else {
      newExpandedBatches.add(batchNumber);
    }
    setExpandedBatches(newExpandedBatches);
  };

  // ✅ CRITICAL FIX: Consistent element ordering for SAA batch validation
  // 🔥 ENHANCED: For multi-lab samples, only show elements routed to SAA lab
  // 🔧 FIX: Get elements for a SPECIFIC sample (for greyed field detection)
  // 🔧 FIX: Must filter by BOTH selected_elements AND element_lab_routing
  const getSampleSpecificElements = (sample: any): string[] => {
    if (!sample) return ['Ag', 'Cd', 'Co', 'Cu', 'Fe', 'Mn', 'Ni', 'Pb', 'Zn'];
    
    // 🔧 CRITICAL FIX: Start with selected_elements, then filter by routing
    // This ensures elements removed per-sample are properly greyed out
    const selectedElements = sample.metadonnees?.selected_elements || [];
    const elementRouting = sample.metadonnees?.element_lab_routing || {};
    const isMultiLab = sample.metadonnees?.multi_lab_workflow === true ||
                       Object.keys(elementRouting).length > 0;
    
    if (isMultiLab && selectedElements.length > 0) {
      // Filter selected_elements to only those routed to SAA lab
      const saaElements = selectedElements.filter((element: string) => 
        elementRouting[element] === 'LABORATOIRE_SAA'
      );
      if (saaElements.length > 0) {
        console.log(`🔍 SAA Supervisor: Elements for ${sample.sample_id} (filtered by selected_elements):`, saaElements);
        return saaElements;
      }
    }
    
    // 2. Direct selected_elements array (for non-multi-lab samples)
    if (selectedElements.length > 0) {
      const validSaaElements = ['Ag', 'Au', 'Cd', 'Co', 'Cu', 'Fe', 'Mn', 'Ni', 'Pb', 'Zn'];
      const saaElements = selectedElements.filter((el: string) => 
        validSaaElements.includes(el)
      );
      if (saaElements.length > 0) return saaElements;
      return selectedElements;
    }
    
    // 3. Fallback to method mapping
    if (sample.metadonnees?.analyses_demandees && Array.isArray(sample.metadonnees.analyses_demandees)) {
      const methodToElements: {[key: string]: string[]} = {
        'AA-PRE.MO/ANA/002': ['Ag', 'Cd', 'Co', 'Cu', 'Fe', 'Mn', 'Ni', 'Pb', 'Zn'],
        'SAA': ['Ag', 'Cd', 'Co', 'Cu', 'Fe', 'Mn', 'Ni', 'Pb', 'Zn'],
        'SAA-MULTI': ['Ag', 'Cd', 'Co', 'Cu', 'Fe', 'Mn', 'Ni', 'Pb', 'Zn'],
      };
      const elements = new Set<string>();
      sample.metadonnees.analyses_demandees.forEach((method: string) => {
        const methodElements = methodToElements[method] || ['Ag', 'Cd', 'Co', 'Cu', 'Fe', 'Mn', 'Ni', 'Pb', 'Zn'];
        methodElements.forEach(el => elements.add(el));
      });
      if (elements.size > 0) return Array.from(elements);
    }
    
    return ['Ag', 'Cd', 'Co', 'Cu', 'Fe', 'Mn', 'Ni', 'Pb', 'Zn'];
  };

  // 🔧 FIX: Get ALL unique elements across entire batch (union of all samples)
  const getSAABatchElementOrder = (samples: any[]): string[] => {
    if (!samples || samples.length === 0) return ['Ag', 'Cd', 'Co', 'Cu', 'Fe', 'Mn', 'Ni', 'Pb', 'Zn'];
    
    const allElements = new Set<string>();
    samples.forEach(sample => {
      const sampleElements = getSampleSpecificElements(sample);
      sampleElements.forEach(el => allElements.add(el));
    });
    
    // Sort: standard SAA order first, then others
    const standardOrder = ['Ag', 'Cd', 'Co', 'Cu', 'Fe', 'Mn', 'Ni', 'Pb', 'Zn'];
    const result = Array.from(allElements).sort((a, b) => {
      const aIndex = standardOrder.indexOf(a);
      const bIndex = standardOrder.indexOf(b);
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return a.localeCompare(b);
    });
    return result;
  };

  // =============================================
  // 📊 QA/QC REFERENCE TABLE (BIS + Blanks + CRM)
  // =============================================

  const getQAQCElements = (): string[] => {
    if (!selectedSample) return ['Ag', 'Cu', 'Fe', 'Pb', 'Zn'];
    const batchSamples = (selectedSample as any)?.batchSamples || [selectedSample];
    return getSAABatchElementOrder(batchSamples);
  };

  const getSAADetectionLimits = (): Record<string, number> => ({
    'Ag': 1, 'Cu': 0.01, 'Fe': 0.01, 'Pb': 0.01, 'Zn': 0.01,
    'Cd': 0.01, 'Co': 0.01, 'Mn': 0.01, 'Ni': 0.01,
  });

  const loadDefaultSAAQAQCTemplate = () => {
    const elements = getQAQCElements();
    const emptyElements: Record<string, string> = {};
    elements.forEach(el => { emptyElements[el] = ''; });

    const blankElements: Record<string, string> = {};
    const limits = getSAADetectionLimits();
    elements.forEach(el => { blankElements[el] = limits[el] ? `${limits[el]}` : ''; });

    const defaultStandards: SAAQAQCStandard[] = [
      { customers_ref: '', lab_ref: '', elements: { ...emptyElements } },
      { customers_ref: 'BIS(...)', lab_ref: '', elements: { ...emptyElements } },
      { customers_ref: 'BIS(...)', lab_ref: '', elements: { ...emptyElements } },
      { customers_ref: 'BIS(...)', lab_ref: '', elements: { ...emptyElements } },
      { customers_ref: 'BLANK (préparation mécanique)', lab_ref: '', elements: { ...blankElements } },
      { customers_ref: 'BLANK (préparation chimique)', lab_ref: '', elements: { ...blankElements } },
      { customers_ref: 'OREAS 139', lab_ref: '', elements: { ...emptyElements } },
      { customers_ref: 'OREAS 603b', lab_ref: '', elements: { ...emptyElements } },
    ];

    setQaqcStandards(defaultStandards);
    setShowQaqcForm(true);
    setFirstBISSampleId(null);
    setSecondBISSampleId(null);
    setThirdBISSampleId(null);
    setFourthBISSampleId(null);
  };

  const autoFillSAABISRow = (sampleId: string, rowIndex: number) => {
    if (!selectedSample) return;
    const batchSamples = (selectedSample as any)?.batchSamples || [];
    const sample = batchSamples.find((s: any) => s.id === sampleId);
    if (!sample) return;

    const elements = getQAQCElements();
    const limits = getSAADetectionLimits();

    const analysisReadings = sample.metadonnees?.saa_analysis_form?.analysis_readings || [];
    const saaCorrections = sample.metadonnees?.saa_analysis?.elements || {};
    const analysisResults = sample.metadonnees?.analysis_results || [];

    const getValue = (element: string): string => {
      const correction = saaCorrections[element];
      if (correction?.correction_applied && correction?.corrected_ppm !== undefined) {
        const ppm = correction.corrected_ppm;
        const dl = limits[element] || 0.01;
        if (ppm < dl) return `${dl}`;
        if (['Ag'].includes(element)) return Math.round(ppm).toString();
        return ppm.toFixed(2);
      }

      let reading = analysisReadings.find((r: any) => r.element === element);
      if (!reading) reading = analysisResults.find((r: any) => r.element === element);
      if (!reading) return '';

      const ppm = reading.ppm || reading.concentration_ppm || 0;
      const dl = limits[element] || 0.01;
      if (ppm === 0 || ppm < dl) return `${dl}`;
      if (['Ag'].includes(element)) return Math.round(ppm).toString();
      return ppm.toFixed(2);
    };

    const customersRefValue = sample.external_id || sample.sample_id;

    const elementValues: Record<string, string> = {};
    elements.forEach(el => { elementValues[el] = getValue(el); });

    const updated = [...qaqcStandards];
    updated[rowIndex] = {
      ...updated[rowIndex],
      customers_ref: customersRefValue,
      lab_ref: sample.sample_id,
      elements: elementValues,
    };
    setQaqcStandards(updated);
    setSnackbarMessage(`✅ Données BIS importées depuis ${sample.sample_id}`);
    setSnackbarOpen(true);
  };

  const handleFirstSAABISChange = (sampleId: string, checked: boolean) => {
    if (checked) {
      if (sampleId === secondBISSampleId) setSecondBISSampleId(null);
      if (sampleId === thirdBISSampleId) setThirdBISSampleId(null);
      if (sampleId === fourthBISSampleId) setFourthBISSampleId(null);
      setFirstBISSampleId(sampleId);
      autoFillSAABISRow(sampleId, 0);
    } else {
      setFirstBISSampleId(null);
      const elements = getQAQCElements();
      const emptyElements: Record<string, string> = {};
      elements.forEach(el => { emptyElements[el] = ''; });
      const updated = [...qaqcStandards];
      updated[0] = { customers_ref: '', lab_ref: '', elements: emptyElements };
      setQaqcStandards(updated);
    }
  };

  const handleSecondSAABISChange = (sampleId: string, checked: boolean) => {
    if (checked) {
      if (sampleId === firstBISSampleId) setFirstBISSampleId(null);
      if (sampleId === thirdBISSampleId) setThirdBISSampleId(null);
      if (sampleId === fourthBISSampleId) setFourthBISSampleId(null);
      setSecondBISSampleId(sampleId);
      autoFillSAABISRow(sampleId, 1);
    } else {
      setSecondBISSampleId(null);
      const elements = getQAQCElements();
      const emptyElements: Record<string, string> = {};
      elements.forEach(el => { emptyElements[el] = ''; });
      const updated = [...qaqcStandards];
      updated[1] = { customers_ref: 'BIS(...)', lab_ref: '', elements: emptyElements };
      setQaqcStandards(updated);
    }
  };

  const handleThirdSAABISChange = (sampleId: string, checked: boolean) => {
    if (checked) {
      if (sampleId === firstBISSampleId) setFirstBISSampleId(null);
      if (sampleId === secondBISSampleId) setSecondBISSampleId(null);
      if (sampleId === fourthBISSampleId) setFourthBISSampleId(null);
      setThirdBISSampleId(sampleId);
      autoFillSAABISRow(sampleId, 2);
    } else {
      setThirdBISSampleId(null);
      const elements = getQAQCElements();
      const emptyElements: Record<string, string> = {};
      elements.forEach(el => { emptyElements[el] = ''; });
      const updated = [...qaqcStandards];
      updated[2] = { customers_ref: 'BIS(...)', lab_ref: '', elements: emptyElements };
      setQaqcStandards(updated);
    }
  };

  const handleFourthSAABISChange = (sampleId: string, checked: boolean) => {
    if (checked) {
      if (sampleId === firstBISSampleId) setFirstBISSampleId(null);
      if (sampleId === secondBISSampleId) setSecondBISSampleId(null);
      if (sampleId === thirdBISSampleId) setThirdBISSampleId(null);
      setFourthBISSampleId(sampleId);
      autoFillSAABISRow(sampleId, 3);
    } else {
      setFourthBISSampleId(null);
      const elements = getQAQCElements();
      const emptyElements: Record<string, string> = {};
      elements.forEach(el => { emptyElements[el] = ''; });
      const updated = [...qaqcStandards];
      updated[3] = { customers_ref: 'BIS(...)', lab_ref: '', elements: emptyElements };
      setQaqcStandards(updated);
    }
  };

  const updateQAQCStandardField = (rowIndex: number, field: string, value: string) => {
    const updated = [...qaqcStandards];
    if (field === 'customers_ref' || field === 'lab_ref') {
      updated[rowIndex] = { ...updated[rowIndex], [field]: value };
    } else {
      updated[rowIndex] = {
        ...updated[rowIndex],
        elements: { ...updated[rowIndex].elements, [field]: value },
      };
    }
    setQaqcStandards(updated);
  };

  return (
    <Box sx={{ width: '100%', bgcolor: 'background.default', minHeight: '100vh' }}>
      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={currentTab} 
          onChange={(_, newValue) => setCurrentTab(newValue)}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<Dashboard />} label="Vue d'Ensemble SAA" />
          <Tab icon={<People />} label="Performance Équipe" />
          <Tab icon={<Assessment />} label="Validation Échantillons" />
        </Tabs>
      </Paper>

      {/* Vue d'Ensemble SAA Tab */}
      {currentTab === 0 && (
        <Box sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
            <Dashboard sx={{ mr: 1, verticalAlign: 'middle' }} />
            Tableau de Bord SAA - Supervision en Temps Réel
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <Card sx={{
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.2)} 100%)`,
                borderLeft: `6px solid ${theme.palette.primary.main}`,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 16px 30px ${alpha(theme.palette.primary.main, 0.3)}`,
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: '100px',
                  height: '100px',
                  background: `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.1)}, transparent)`,
                  borderRadius: '50%',
                  transform: 'translate(50%, -50%)',
                }
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom sx={{ fontWeight: 500 }}>Échantillons en Laboratoire</Typography>
                      <Typography variant="h4" sx={{
                        background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        fontWeight: 'bold'
                      }}>
                    {labStatus?.total_samples_in_lab || 0}
                  </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Total dans SAA
                  </Typography>
                    </Box>
                    <Box sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.8rem',
                      boxShadow: `0 8px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
                      transition: 'transform 0.2s ease',
                      '&:hover': { transform: 'scale(1.1)' }
                    }}>
                      🧪
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card sx={{
                background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(theme.palette.warning.main, 0.2)} 100%)`,
                borderLeft: `6px solid ${theme.palette.warning.main}`,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 16px 30px ${alpha(theme.palette.warning.main, 0.3)}`,
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: '100px',
                  height: '100px',
                  background: `linear-gradient(45deg, ${alpha(theme.palette.warning.main, 0.1)}, transparent)`,
                  borderRadius: '50%',
                  transform: 'translate(50%, -50%)',
                }
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom sx={{ fontWeight: 500 }}>En Validation</Typography>
                      <Typography variant="h4" sx={{
                        background: `linear-gradient(45deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        fontWeight: 'bold'
                      }}>
                    {labStatus?.samples_validation_pending || 0}
                  </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Attente validation
                  </Typography>
                    </Box>
                    <Box sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.8rem',
                      boxShadow: `0 8px 16px ${alpha(theme.palette.warning.main, 0.3)}`,
                      transition: 'transform 0.2s ease',
                      '&:hover': { transform: 'scale(1.1)' }
                    }}>
                      ⏳
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card sx={{
                background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.main, 0.2)} 100%)`,
                borderLeft: `6px solid ${theme.palette.success.main}`,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 16px 30px ${alpha(theme.palette.success.main, 0.3)}`,
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: '100px',
                  height: '100px',
                  background: `linear-gradient(45deg, ${alpha(theme.palette.success.main, 0.1)}, transparent)`,
                  borderRadius: '50%',
                  transform: 'translate(50%, -50%)',
                }
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom sx={{ fontWeight: 500 }}>Instruments Actifs</Typography>
                      <Typography variant="h4" sx={{
                        background: `linear-gradient(45deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        fontWeight: 'bold'
                      }}>
                    {labStatus?.instruments_active || 0}/{labStatus?.instruments_total || 0}
                  </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Opérationnels
                  </Typography>
                    </Box>
                    <Box sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.8rem',
                      boxShadow: `0 8px 16px ${alpha(theme.palette.success.main, 0.3)}`,
                      transition: 'transform 0.2s ease',
                      '&:hover': { transform: 'scale(1.1)' }
                    }}>
                      ⚡
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card sx={{
                background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(theme.palette.info.main, 0.2)} 100%)`,
                borderLeft: `6px solid ${theme.palette.info.main}`,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 16px 30px ${alpha(theme.palette.info.main, 0.3)}`,
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: '100px',
                  height: '100px',
                  background: `linear-gradient(45deg, ${alpha(theme.palette.info.main, 0.1)}, transparent)`,
                  borderRadius: '50%',
                  transform: 'translate(50%, -50%)',
                }
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom sx={{ fontWeight: 500 }}>Charge de Travail</Typography>
                      <Typography variant="h4" sx={{
                        background: `linear-gradient(45deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        fontWeight: 'bold'
                      }}>
                    {labStatus?.current_workload_percent || 0}%
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={labStatus?.current_workload_percent || 0} 
                        sx={{ 
                          mt: 1,
                          background: `linear-gradient(90deg, ${alpha(theme.palette.info.main, 0.2)}, ${alpha(theme.palette.info.main, 0.1)})`,
                          '& .MuiLinearProgress-bar': {
                            background: `linear-gradient(90deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`
                          }
                        }}
                      />
                    </Box>
                    <Box sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.8rem',
                      boxShadow: `0 8px 16px ${alpha(theme.palette.info.main, 0.3)}`,
                      transition: 'transform 0.2s ease',
                      '&:hover': { transform: 'scale(1.1)' }
                    }}>
                      📊
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Performance Équipe Tab */}
      {currentTab === 1 && (
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            <People sx={{ mr: 1, verticalAlign: 'middle' }} />
            Performance de l'Équipe SAA
          </Typography>

          {Array.isArray(staffPerformance) && staffPerformance.length > 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Nom</TableCell>
                    <TableCell>Rôle</TableCell>
                    <TableCell align="right">Aujourd'hui</TableCell>
                    <TableCell align="right">Cette Semaine</TableCell>
                    <TableCell align="right">Ce Mois</TableCell>
                    <TableCell align="right">Temps Moyen</TableCell>
                    <TableCell align="right">Score Qualité</TableCell>
                    <TableCell align="center">Tendance</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {staffPerformance.map((staff) => (
                    <TableRow key={staff.user_id}>
                      <TableCell>{staff.user_name}</TableCell>
                      <TableCell>
                        <Chip 
                          label={staff.user_role} 
                          size="small" 
                          color={staff.user_role === 'ANALYST' ? 'primary' : 'secondary'}
                        />
                      </TableCell>
                      <TableCell align="right">{staff.samples_completed_today}</TableCell>
                      <TableCell align="right">{staff.samples_completed_week}</TableCell>
                      <TableCell align="right">{staff.samples_completed_month}</TableCell>
                      <TableCell align="right">{staff.average_completion_time_hours.toFixed(1)}h</TableCell>
                      <TableCell align="right">
                        <Typography 
                          color={staff.quality_score_percent >= 95 ? 'success.main' : 'warning.main'}
                          fontWeight={600}
                        >
                          {staff.quality_score_percent.toFixed(1)}%
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {staff.efficiency_trend === 'UP' && <TrendingUp color="success" />}
                        {staff.efficiency_trend === 'DOWN' && <TrendingDown color="error" />}
                        {staff.efficiency_trend === 'STABLE' && <Schedule color="info" />}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info">
              Aucune donnée de performance disponible pour l'équipe SAA
            </Alert>
          )}
        </Box>
      )}

      {/* Validation Échantillons Tab */}
      {currentTab === 2 && (
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            <Assessment sx={{ mr: 1, verticalAlign: 'middle' }} />
            Validation des Échantillons SAA
          </Typography>

          {Array.isArray(samples) && samples.length > 0 ? (
            <Box>
              {(() => {
                const { batches, individualSamples } = groupSamplesByBatch(samples);
                const hasBatches = Object.keys(batches).length > 0;
                const hasIndividual = individualSamples.length > 0;

                return (
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>N° Échantillon / Lot</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Client</TableCell>
                          <TableCell>Priorité</TableCell>
                          <TableCell>Analyste</TableCell>
                          <TableCell>Date Analyse</TableCell>
                          <TableCell>Statut</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {/* Render Batch Samples */}
                        {Object.entries(batches).map(([batchNumber, batchSamples]) => {
                          const totalWeight = batchSamples.reduce((sum, sample) => {
                            const weight = sample.metadonnees?.saa_preparation?.initialWeight || 0;
                            return sum + weight;
                          }, 0);

                          // ✅ NOTE: This is just for display purposes in batch header, not for column generation
                          const elementsAnalyzed = batchSamples[0]?.metadonnees?.batch_analysis_summary?.elements_analyzed || [];
                          const belowLODCount = batchSamples[0]?.metadonnees?.batch_analysis_summary?.below_lod_count || 0;
                          const isExpanded = expandedBatches.has(batchNumber);

                          return (
                            <React.Fragment key={batchNumber}>
                              {/* Batch Header Row - Now Clickable */}
                              <TableRow 
                                sx={{ 
                                  backgroundColor: '#e3f2fd',
                                  cursor: 'pointer',
                                  '&:hover': { backgroundColor: '#bbdefb' }
                                }}
                                onClick={() => toggleBatchExpansion(batchNumber)}
                              >
                                <TableCell colSpan={8}>
                                  <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <Box display="flex" alignItems="center" gap={2}>
                                      <IconButton size="small" sx={{ p: 0 }}>
                                        {isExpanded ? <ExpandLess /> : <ExpandMore />}
                                      </IconButton>
                                      <Chip 
                                        label="LOT SAA VALIDATION" 
                                        color="primary" 
                                        variant="filled"
                                        sx={{ 
                                          fontWeight: 'bold',
                                          backgroundColor: '#1976d2',
                                          color: 'white'
                                        }}
                                      />
                                      <Typography variant="h6" fontWeight="bold" color="primary">
                                        {batchNumber}
                                      </Typography>
                                      <Chip 
                                        label={`${batchSamples.length} échantillons`} 
                                        size="small" 
                                        color="primary" 
                                        variant="outlined"
                                      />
                                      <Chip 
                                        label={`${totalWeight.toFixed(2)} g total`} 
                                        size="small" 
                                        color="info" 
                                        variant="outlined"
                                      />
                                      {elementsAnalyzed.length > 0 && (
                                        <Box display="flex" gap={0.5}>
                                          {elementsAnalyzed.slice(0, 4).map((element: string) => (
                                            <Chip key={element} label={element} size="small" sx={{ fontSize: '0.7rem' }} />
                                          ))}
                                          {elementsAnalyzed.length > 4 && (
                                            <Chip label={`+${elementsAnalyzed.length - 4}`} size="small" />
                                          )}
                                        </Box>
                                      )}
                                      {belowLODCount > 0 && (
                                        <Chip 
                                          label={`⚠️ ${belowLODCount} < LOD`} 
                                          size="small" 
                                          color="error"
                                        />
                                      )}
                                    </Box>
                                    <Button
                                      variant="contained"
                                      size="small"
                                      startIcon={<VerifiedUser />}
                                      onClick={(e) => {
                                        e.stopPropagation(); // Prevent batch expansion when clicking button
                                        handleValidateBatch(batchSamples);
                                      }}
                                      color="primary"
                                      sx={{ fontWeight: 'bold' }}
                                    >
                                      Valider le Lot
                                    </Button>
                                  </Box>
                                </TableCell>
                              </TableRow>

                              {/* Individual Batch Sample Rows - Only show when expanded */}
                              {isExpanded && batchSamples.map((sample, index) => (
                                <TableRow 
                                  key={sample.id}
                                  sx={{ 
                                    backgroundColor: '#f8f9ff',
                                    '&:hover': { backgroundColor: '#f0f4ff' }
                                  }}
                                >
                                  <TableCell sx={{ pl: 4 }}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                      <Typography variant="body2" fontWeight={600}>
                                        {sample.sample_id}
                                      </Typography>
                                      <Chip 
                                        label={`Sac ${getBatchPosition(sample)}/${getTotalSamplesInBatch(sample)}`}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        sx={{ fontSize: '0.65rem' }}
                                      />
                                    </Box>
                                  </TableCell>
                                  <TableCell>{sample.type_echantillon}</TableCell>
                                  <TableCell>{sample.client?.company_name}</TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={sample.priorite} 
                                      size="small" 
                                      color={getPriorityColor(sample.priorite)}
                                    />
                                  </TableCell>
                                  <TableCell>{sample.analyst_name || sample.metadonnees?.saa_analyst_name || 'SAA Analyst'}</TableCell>
                                  <TableCell>{new Date(sample.date_modification).toLocaleDateString('fr-FR')}</TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={sample.statut} 
                                      size="small" 
                                      color="info"
                                    />
                                  </TableCell>
                                  <TableCell align="center">
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      startIcon={<CheckCircle />}
                                      onClick={() => handleValidateSample(sample)}
                                      color="primary"
                                    >
                                      Individuel
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </React.Fragment>
                          );
                        })}

                        {/* Render Individual Samples */}
                        {individualSamples.map((sample) => (
                          <TableRow key={sample.id}>
                            <TableCell>
                              <Typography fontWeight={600}>{sample.sample_id}</Typography>
                            </TableCell>
                            <TableCell>{sample.type_echantillon}</TableCell>
                            <TableCell>{sample.client?.company_name}</TableCell>
                            <TableCell>
                              <Chip 
                                label={sample.priorite} 
                                size="small" 
                                color={getPriorityColor(sample.priorite)}
                              />
                            </TableCell>
                            <TableCell>{sample.analyst_name || sample.metadonnees?.saa_analyst_name || 'SAA Analyst'}</TableCell>
                            <TableCell>{new Date(sample.date_modification).toLocaleDateString('fr-FR')}</TableCell>
                            <TableCell>
                              <Chip 
                                label={sample.statut} 
                                size="small" 
                                color="info"
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<CheckCircle />}
                                onClick={() => handleValidateSample(sample)}
                                color="primary"
                              >
                                Valider
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                );
              })()}
            </Box>
          ) : (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>ℹ️ Aucun Échantillon en Validation</Typography>
                <Typography variant="body2">
                  Il n'y a actuellement aucun échantillon avec le statut "VALIDATION_SERVICE" pour le laboratoire SAA.
                  Les échantillons doivent d'abord être analysés par les analystes avant d'apparaître ici pour validation.
                </Typography>
              </Alert>
            </Box>
          )}
        </Box>
      )}

      {/* Enhanced Sample Validation Dialog with Integrated Quality Control - Optimized for Large Datasets */}
      <Dialog 
        open={validationDialog} 
        onClose={() => setValidationDialog(false)}
        maxWidth={false}
        fullScreen
        PaperProps={{ 
          sx: { 
            backgroundColor: '#f8f9fa',
            '& .MuiDialogTitle-root': {
              backgroundColor: '#1976d2',
              color: 'white',
              position: 'sticky',
              top: 0,
              zIndex: 1000
            }
          } 
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={2}>
              <VerifiedUser sx={{ fontSize: '1.8rem' }} />
              <Box>
                <Typography variant="h5" sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
            {(selectedSample as any)?.isBatchValidation 
                    ? `🔍 Validation Lot SAA: ${selectedSample ? getBatchNumber(selectedSample) : 'N/A'}`
                    : `🔍 Validation Échantillon: ${selectedSample?.sample_id}`
            }
          </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.8rem' }}>
            {(selectedSample as any)?.isBatchValidation 
              ? `${(selectedSample as any)?.batchSamples?.length || 0} échantillons dans le lot`
              : `Type: ${selectedSample?.type_echantillon} | Client: ${selectedSample?.client?.company_name}`
            }
          </Typography>
              </Box>
            </Box>
            
            {/* Status Indicator */}
            <Box display="flex" alignItems="center" gap={2}>
              <Chip 
                label={selectedSample?.priorite || 'NORMALE'} 
                size="small" 
                color={getPriorityColor((selectedSample?.priorite || 'NORMALE') as 'NORMALE' | 'URGENTE' | 'CRITIQUE')}
                sx={{ 
                  color: 'white', 
                  borderColor: 'white',
                  fontSize: '0.75rem'
                }}
              />
            </Box>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
          {selectedSample && (
            <Box sx={{ mt: 1 }}>
              {(selectedSample as any)?.isBatchValidation ? (
                /* Batch Validation View - Enhanced with Complete Information */
                <Box>
                  {/* Comprehensive Batch Information Section */}
                  <Card sx={{ mb: 2, backgroundColor: '#e8f5e8', border: '2px solid #4caf50' }}>
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="h6" sx={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#2e7d32', mb: 2 }}>
                        📊 Informations Complètes du Lot {selectedSample ? getBatchNumber(selectedSample) : 'N/A'}
                      </Typography>
                      
                      <Grid container spacing={2}>
                        {/* Batch Summary */}
                        <Grid item xs={12} sm={6} md={3}>
                          <Card variant="outlined" sx={{ p: 1, backgroundColor: 'white' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Échantillons</Typography>
                            <Typography variant="h6" color="primary" sx={{ fontSize: '1.2rem' }}>
                              {(selectedSample as any)?.batchSamples?.length || 0}
                            </Typography>
                          </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <Card variant="outlined" sx={{ p: 1, backgroundColor: 'white' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Poids Total</Typography>
                            <Typography variant="h6" color="secondary" sx={{ fontSize: '1.2rem' }}>
                              {(() => {
                                const totalWeight = ((selectedSample as any)?.batchSamples || []).reduce((sum: number, sample: any) => {
                              const weight = sample.metadonnees?.saa_preparation?.initialWeight || 0;
                              return sum + weight;
                                }, 0);
                                return `${totalWeight.toFixed(2)} g`;
                              })()}
                          </Typography>
                          </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <Card variant="outlined" sx={{ p: 1, backgroundColor: 'white' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Client</Typography>
                            <Typography variant="body1" sx={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                              {selectedSample?.client?.company_name || 'N/A'}
                          </Typography>
                          </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <Card variant="outlined" sx={{ p: 1, backgroundColor: 'white' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Priorité</Typography>
                            <Chip 
                              label={selectedSample?.priorite || 'NORMALE'} 
                              color={getPriorityColor(selectedSample?.priorite || 'NORMALE')} 
                              size="small" 
                              sx={{ fontWeight: 'bold' }}
                            />
                          </Card>
                        </Grid>
                        
                        {/* Sample Context Information */}
                        <Grid item xs={12} sm={6}>
                          <Card variant="outlined" sx={{ p: 1, backgroundColor: 'white' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Projet / Site</Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                              <strong>Projet:</strong> {selectedSample?.nom_projet || 'Non spécifié'}<br/>
                              <strong>Site:</strong> {selectedSample?.site_minier || 'Non spécifié'}
                          </Typography>
                          </Card>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Card variant="outlined" sx={{ p: 1, backgroundColor: 'white' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Dates</Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                              <strong>Réception:</strong> {new Date(selectedSample?.date_reception || '').toLocaleDateString('fr-FR')}<br/>
                              <strong>Analyse:</strong> {selectedSample?.metadonnees?.saa_analysis_completed_at 
                                ? new Date(selectedSample.metadonnees.saa_analysis_completed_at).toLocaleDateString('fr-FR')
                                : 'N/A'
                              }
                            </Typography>
                          </Card>
                      </Grid>

                        {/* Elements and Methods */}
                        <Grid item xs={12}>
                          <Card variant="outlined" sx={{ p: 1, backgroundColor: 'white' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 1 }}>
                              Éléments Analysés et Informations Techniques
                        </Typography>
                            <Box display="flex" gap={1} flexWrap="wrap" sx={{ mb: 1 }}>
                              {/* ✅ NOTE: This is just for display purposes in validation dialog, not for column generation */}
                              {(selectedSample?.metadonnees?.batch_analysis_summary?.elements_analyzed || getSAABatchElementOrder([selectedSample])).map((element: string) => (
                                <Chip 
                                  key={element} 
                                  label={element} 
                                  size="small"
                                  color="primary" 
                                  variant="filled"
                                  sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}
                                />
                          ))}
                        </Box>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                              <strong>Méthode:</strong> {selectedSample?.metadonnees?.batch_analysis_summary?.detection_method || 'Spectrométrie d\'Absorption Atomique'} | 
                              <strong> Standard:</strong> {selectedSample?.metadonnees?.batch_analysis_summary?.reference_standard || 'Matériau de Référence Certifié'} | 
                              <strong> Analyses:</strong> {selectedSample?.metadonnees?.batch_analysis_summary?.total_readings || 0} lectures
                            </Typography>
                          </Card>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>

                  {/* Compact Batch Summary */}
                  <Card sx={{ mb: 2, backgroundColor: '#e3f2fd' }}>
                    <CardContent sx={{ p: 2 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                        <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#1976d2' }}>
                          📊 Résultats d'Analyse - Lot {selectedSample ? getBatchNumber(selectedSample) : 'N/A'}
                              </Typography>
                        
                        {/* Quality Indicators */}
                        <Box display="flex" gap={1}>
                          <Chip 
                            label={`${selectedSample?.metadonnees?.batch_analysis_summary?.below_lod_count || 0} < LOD`}
                            color={selectedSample?.metadonnees?.batch_analysis_summary?.below_lod_count > 0 ? "error" : "success"}
                            size="small"
                            variant="outlined"
                          />
                          <Chip 
                            label="Tous résultats OK"
                            color="success"
                            size="small"
                            variant="filled"
                          />
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>

                  {/* 🔬 QC CORRECTION SECTION - Coefficient de Correction */}
                  <Card sx={{ mb: 2, backgroundColor: '#fff3e0', border: '2px solid #ff9800' }}>
                    <CardContent sx={{ p: 2 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#e65100' }}>
                          🔬 Contrôle Qualité & Coefficient de Correction
                        </Typography>
                        <Box display="flex" gap={1} alignItems="center">
                          <FormControlLabel
                            control={
                              <Switch
                                checked={showCorrectedValues}
                                onChange={(e) => setShowCorrectedValues(e.target.checked)}
                                color="warning"
                              />
                            }
                            label={<Typography variant="body2" sx={{ fontSize: '0.8rem' }}>Afficher valeurs corrigées</Typography>}
                          />
                          {qcLoading && <CircularProgress size={20} />}
                        </Box>
                      </Box>
                      
                      {batchQCData && Object.keys(batchQCData.qcData).length > 0 ? (
                        <Box>
                          {/* QC Summary Table */}
                          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, maxHeight: 250 }}>
                            <Table size="small" stickyHeader>
                              <TableHead>
                                <TableRow sx={{ backgroundColor: '#ffecb3' }}>
                                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', backgroundColor: '#ffecb3' }}>Élément</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.75rem', backgroundColor: '#ffecb3' }}>TQ Lecture</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.75rem', backgroundColor: '#e3f2fd' }}>TQ Calculé</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.75rem', backgroundColor: '#ffecb3' }}>TQ (n)</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.75rem', backgroundColor: '#ffecb3' }}>RSD%</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.75rem', backgroundColor: '#e8f5e9' }}>Val. Certifiée</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.75rem', backgroundColor: '#fff3e0' }}>Récupération</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.75rem', backgroundColor: '#ffe0b2' }}>Coef. Correction</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.75rem', backgroundColor: '#ffecb3' }}>Action</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {Object.entries(batchQCData.qcData).map(([element, qc]) => {
                                  const qcElement = qc as QCElementData;
                                  const certified = batchQCData.certifiedValues[element];
                                  const unit = certified?.unit || 'ppm';
                                  
                                  // Use pre-calculated values from loadBatchQCData
                                  const recoveryPercent = qcElement.recovery_percent;
                                  const isRecoveryOk = recoveryPercent && recoveryPercent >= 90 && recoveryPercent <= 110;
                                  const needsCorrection = recoveryPercent && (recoveryPercent < 95 || recoveryPercent > 105);
                                  
                                  return (
                                    <TableRow key={element} sx={{ 
                                      backgroundColor: needsCorrection ? alpha('#ff9800', 0.1) : 'inherit',
                                      '&:hover': { backgroundColor: alpha('#ff9800', 0.15) }
                                    }}>
                                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{element}</TableCell>
                                      <TableCell align="center" sx={{ fontSize: '0.75rem' }}>
                                        {qcElement.tq_average?.toFixed(4) || '-'}
                                      </TableCell>
                                      <TableCell align="center" sx={{ fontSize: '0.75rem', backgroundColor: alpha('#2196f3', 0.05) }}>
                                        {qcElement.tq_calculated?.toFixed(4) || '-'} {unit}
                                      </TableCell>
                                      <TableCell align="center" sx={{ fontSize: '0.75rem' }}>{qcElement.tq_count || 0}</TableCell>
                                      <TableCell align="center" sx={{ 
                                        fontSize: '0.75rem',
                                        color: qcElement.tq_rsd > 5 ? '#f44336' : '#4caf50',
                                        fontWeight: qcElement.tq_rsd > 5 ? 'bold' : 'normal'
                                      }}>
                                        {qcElement.tq_rsd?.toFixed(1) || '-'}%
                                      </TableCell>
                                      <TableCell align="center" sx={{ fontSize: '0.75rem', backgroundColor: alpha('#4caf50', 0.05), p: 0.5 }}>
                                        <TextField
                                          type="number"
                                          size="small"
                                          value={editedCertifiedValues[element] ?? qcElement.certified_value ?? ''}
                                          onChange={(e) => {
                                            const val = parseFloat(e.target.value);
                                            if (!isNaN(val) && val > 0) {
                                              handleCertifiedValueChange(element, val);
                                            }
                                          }}
                                          InputProps={{
                                            endAdornment: <Typography variant="caption" sx={{ ml: 0.5, color: 'text.secondary' }}>{unit}</Typography>,
                                            sx: { fontSize: '0.75rem', py: 0 }
                                          }}
                                          inputProps={{ 
                                            step: unit === '%' ? 0.01 : 1,
                                            min: 0,
                                            style: { textAlign: 'center', padding: '4px 8px', width: '70px' }
                                          }}
                                          sx={{ 
                                            '& .MuiOutlinedInput-root': { 
                                              backgroundColor: editedCertifiedValues[element] ? alpha('#ff9800', 0.1) : 'white',
                                              '& fieldset': { borderColor: editedCertifiedValues[element] ? '#ff9800' : undefined }
                                            }
                                          }}
                                        />
                                      </TableCell>
                                      <TableCell align="center" sx={{ 
                                        fontSize: '0.85rem', 
                                        fontWeight: 'bold',
                                        color: needsCorrection ? '#e65100' : '#4caf50',
                                        backgroundColor: needsCorrection ? alpha('#ff9800', 0.2) : alpha('#4caf50', 0.1)
                                      }}>
                                        {qcElement.correction_coefficient?.toFixed(4) || '1.0000'}
                                      </TableCell>
                                      <TableCell align="center">
                                        <Button
                                          size="small"
                                          variant="contained"
                                          color={needsCorrection ? 'warning' : 'success'}
                                          disabled={!qcElement.correction_coefficient}
                                          onClick={() => {
                                            if (qcElement.correction_coefficient) {
                                              applyElementCorrection(
                                                element,
                                                qcElement.correction_coefficient,
                                                (selectedSample as any)?.batchSamples || []
                                              );
                                            }
                                          }}
                                          sx={{ fontSize: '0.65rem', py: 0.25, px: 1, minWidth: 'auto' }}
                                        >
                                          {needsCorrection ? 'Corriger' : 'OK'}
                                        </Button>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </TableContainer>
                          
                          {/* Legend */}
                          <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary', fontStyle: 'italic' }}>
                            💡 <strong>Formule: Coef = Valeur Certifiée / TQ Calculé</strong> (où TQ Calculé = Lecture × V/p × VD/PD) | 
                            Récupération 90-110% = OK, 95-105% = Optimal. Cliquez "Corriger" si nécessaire.
                            {Object.keys(correctedValues).length > 0 && (
                              <Chip 
                                label={`${Object.keys(correctedValues).length} échantillons corrigés`}
                                color="warning"
                                size="small"
                                sx={{ ml: 1 }}
                              />
                            )}
                          </Typography>
                        </Box>
                      ) : (
                        <Alert severity="info" sx={{ fontSize: '0.8rem' }}>
                          {qcLoading ? 'Chargement des données QC...' : 'Aucune donnée QC disponible pour ce lot. Les valeurs TQ n\'ont peut-être pas été importées lors de l\'analyse.'}
                        </Alert>
                      )}
                    </CardContent>
                  </Card>

                  {/* SAA Formula Explanation - Updated with Dynamic Formula */}
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 'bold', mb: 1 }}>
                      🧮 Formule SAA Utilisée: <strong>
                        {selectedSample?.metadonnees?.batch_analysis_summary?.dilution_formula || 'L × (V/p) × (vf/vp)'}
                      </strong>
                      {(() => {
                        const dilutionChoice = selectedSample?.metadonnees?.batch_analysis_summary?.dilution_choice ?? 1;
                        return (
                          <Chip 
                            label={`Choix ${dilutionChoice}`}
                            size="small"
                            color={dilutionChoice === 0 ? 'default' : dilutionChoice === 2 ? 'secondary' : 'primary'}
                            sx={{ ml: 1 }}
                          />
                        );
                      })()}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                      <strong>V</strong> = Volume initial après digestion (mL) | 
                      <strong>p</strong> = Poids initial échantillon (g) | 
                      <strong>vf</strong> = Volume final après dilutions (mL) | 
                      <strong>vp</strong> = Volume prélevé pour dilutions (mL)
                      {(() => {
                        const dilutionChoice = selectedSample?.metadonnees?.batch_analysis_summary?.dilution_choice ?? 1;
                        if (dilutionChoice === 2) {
                          return (
                            <span>
                              {' | '}
                              <strong>vf1</strong> = Volume final alternatif (mL) | 
                              <strong>vp1</strong> = Volume prélevé alternatif (mL)
                            </span>
                          );
                        }
                        return null;
                      })()}
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.75rem', display: 'block', mt: 1, fontStyle: 'italic' }}>
                      💡 Choix 0: Simplifié L×(V/p) | Choix 1: Complet L×(V/p)×(vf/vp) | Choix 2: Alternatif L×(V/p)×(vf1/vp1)
                    </Typography>
                  </Alert>

                  {/* Enhanced Analysis Results Table */}
                  <Card sx={{ mb: 3 }}>
                    <CardContent sx={{ p: 1 }}>
                      <TableContainer>
                        <Table size="small" sx={{ fontSize: '0.7rem' }}>
                          <TableHead>
                            {/* FIRST ROW: Element Headers spanning across their columns */}
                            <TableRow>
                              <TableCell rowSpan={2} align="center" sx={{ 
                                fontWeight: 'bold', 
                                backgroundColor: '#1976d2',
                                color: 'white',
                                fontSize: '0.75rem',
                                padding: '8px 6px',
                                textAlign: 'center',
                                minWidth: '100px',
                                verticalAlign: 'middle'
                              }}>
                                Échantillon
                              </TableCell>
                              <TableCell rowSpan={2} align="center" sx={{ 
                                fontWeight: 'bold', 
                                backgroundColor: '#1976d2',
                                color: 'white',
                                fontSize: '0.75rem',
                                padding: '8px 6px',
                                textAlign: 'center',
                                minWidth: '80px',
                                verticalAlign: 'middle'
                              }}>
                                Poids
                                <br />
                                (g)
                              </TableCell>
                              <TableCell rowSpan={2} align="center" sx={{ 
                                fontWeight: 'bold', 
                                backgroundColor: '#1976d2',
                                color: 'white',
                                fontSize: '0.75rem',
                                padding: '8px 6px',
                                textAlign: 'center',
                                minWidth: '100px',
                                verticalAlign: 'middle'
                              }}>
                                Réf. Client
                              </TableCell>
                              
                              {/* SAA Preparation Variables */}
                              <TableCell colSpan={6} align="center" sx={{ 
                                fontWeight: 'bold', 
                                backgroundColor: '#ff9800',
                                color: 'white',
                                fontSize: '0.8rem',
                                padding: '8px 6px',
                                textAlign: 'center',
                                borderLeft: '2px solid #f57c00'
                              }}>
                                Variables Formule SAA
                              </TableCell>
                              
                              {/* Element Headers - Each element spans 4 columns */}
                              {(() => {
                                const elementsToShow = getSAABatchElementOrder((selectedSample as any)?.batchSamples || [selectedSample]);
                                
                                return elementsToShow.map((element: string, index: number) => (
                                  <TableCell 
                                    key={`${element}-group-header`} 
                                    colSpan={5}  // 🔬 Updated: 5 columns (Lecture, Dilution, Choix, Concentration, Corrigé)
                                    align="center" 
                                    sx={{ 
                                      fontWeight: 'bold', 
                                      backgroundColor: '#4caf50',
                                      color: 'white',
                                      fontSize: '0.9rem',
                                      padding: '8px 6px',
                                      textAlign: 'center',
                                      borderLeft: '3px solid #2e7d32',
                                      borderRight: '3px solid #2e7d32'
                                    }}
                                  >
                                    {element}
                                  </TableCell>
                                ));
                              })()}
                            </TableRow>
                            
                            {/* SECOND ROW: Sub-headers for preparation variables and element data */}
                            <TableRow>
                              {/* SAA Preparation Sub-headers */}
                              <TableCell align="center" sx={{ 
                                fontWeight: 'bold', 
                                backgroundColor: '#ff9800',
                                color: 'white',
                                fontSize: '0.7rem',
                                padding: '6px 4px',
                                textAlign: 'center',
                                minWidth: '70px'
                              }}>
                                V (mL)
                              </TableCell>
                              <TableCell align="center" sx={{ 
                                fontWeight: 'bold', 
                                backgroundColor: '#ff9800',
                                color: 'white',
                                fontSize: '0.7rem',
                                padding: '6px 4px',
                                textAlign: 'center',
                                minWidth: '70px'
                              }}>
                                p (g)
                              </TableCell>
                              <TableCell align="center" sx={{ 
                                fontWeight: 'bold', 
                                backgroundColor: '#ff9800',
                                color: 'white',
                                fontSize: '0.7rem',
                                padding: '6px 4px',
                                textAlign: 'center',
                                minWidth: '70px'
                              }}>
                                vf (mL)
                              </TableCell>
                              <TableCell align="center" sx={{ 
                                fontWeight: 'bold', 
                                backgroundColor: '#ff9800',
                                color: 'white',
                                fontSize: '0.7rem',
                                padding: '6px 4px',
                                textAlign: 'center',
                                minWidth: '70px'
                              }}>
                                vp (mL)
                              </TableCell>
                              <TableCell align="center" sx={{ 
                                fontWeight: 'bold', 
                                backgroundColor: '#9c27b0',
                                color: 'white',
                                fontSize: '0.7rem',
                                padding: '6px 4px',
                                textAlign: 'center',
                                minWidth: '70px'
                              }}>
                                vf1 (mL)
                              </TableCell>
                              <TableCell align="center" sx={{ 
                                fontWeight: 'bold', 
                                backgroundColor: '#9c27b0',
                                color: 'white',
                                fontSize: '0.7rem',
                                padding: '6px 4px',
                                textAlign: 'center',
                                minWidth: '70px'
                              }}>
                                vp1 (mL)
                              </TableCell>
                              
                              {/* Element Sub-headers - 4 columns per element */}
                              {(() => {
                                const elementsToShow = getSAABatchElementOrder((selectedSample as any)?.batchSamples || [selectedSample]);
                                
                                return elementsToShow.flatMap((element: string) => [
                                  <TableCell key={`${element}-lecture-header`} align="center" sx={{ 
                                    fontWeight: 'bold', 
                                    backgroundColor: '#66bb6a',
                                    color: 'white',
                                    fontSize: '0.7rem',
                                    padding: '6px 4px',
                                    borderLeft: '2px solid #4caf50'
                                  }}>
                                    Lecture
                                  </TableCell>,
                                  <TableCell key={`${element}-dilution-header`} align="center" sx={{ 
                                    fontWeight: 'bold', 
                                    backgroundColor: '#42a5f5',
                                    color: 'white',
                                    fontSize: '0.7rem',
                                    padding: '6px 4px'
                                  }}>
                                    Dilution
                                  </TableCell>,
                                  <TableCell key={`${element}-choice-header`} align="center" sx={{ 
                                    fontWeight: 'bold', 
                                    backgroundColor: '#ab47bc',
                                    color: 'white',
                                    fontSize: '0.7rem',
                                    padding: '6px 4px'
                                  }}>
                                    Choix
                                  </TableCell>,
                                  <TableCell key={`${element}-concentration-header`} align="center" sx={{ 
                                    fontWeight: 'bold', 
                                    backgroundColor: '#ffa726',
                                    color: 'white',
                                    fontSize: '0.7rem',
                                    padding: '6px 4px'
                                  }}>
                                    Concentration
                                  </TableCell>,
                                  <TableCell key={`${element}-corrected-header`} align="center" sx={{ 
                                    fontWeight: 'bold', 
                                    backgroundColor: '#e91e63',
                                    color: 'white',
                                    fontSize: '0.7rem',
                                    padding: '6px 4px',
                                    borderRight: '2px solid #e91e63'
                                  }}>
                                    Corrigé
                                  </TableCell>
                                ]);
                              })()}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {((selectedSample as any)?.batchSamples || [])
                              .sort((a: any, b: any) => {
                                // Smart numeric sorting
                                const aNum = parseInt(a.sample_id.replace(/\D/g, ''));
                                const bNum = parseInt(b.sample_id.replace(/\D/g, ''));
                                if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum;
                                return a.sample_id.localeCompare(b.sample_id);
                              })
                              .map((sample: any, index: number) => (
                                <TableRow 
                                  key={sample.id}
                                  sx={{ 
                                  backgroundColor: index % 2 === 0 ? '#f9f9f9' : 'white',
                                  '&:hover': { backgroundColor: '#f0f8ff' },
                                  '& .MuiTableCell-root': {
                                    padding: '3px 4px',
                                    fontSize: '0.7rem'
                                  }
                                  }}
                                >
                                {/* Sample ID - Ultra Compact */}
                                  <TableCell align="center">
                                  <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.75rem', textAlign: 'center' }}>
                                        {sample.sample_id}
                                      </Typography>
                                  </TableCell>
                                  
                                {/* Weight - Compact */}
                                  <TableCell align="center">
                                  <Typography variant="body2" sx={{ fontSize: '0.75rem', textAlign: 'center' }}>
                                    {sample.metadonnees?.saa_preparation?.initialWeight?.toFixed(2) || 'N/A'}
                                    </Typography>
                                  </TableCell>
                                  
                                {/* Client Reference - MISSING COLUMN ADDED */}
                                  <TableCell align="center">
                                  <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.75rem', color: '#1976d2', textAlign: 'center' }}>
                                    {sample.external_id || 'N/A'}
                                    </Typography>
                                  </TableCell>
                                  
                                {/* SAA Preparation Data */}
                                {(() => {
                                  const prepData = sample.metadonnees?.saa_analysis_form?.preparation_data || sample.metadonnees?.saa_preparation;
                                  const dilution = prepData?.dilutions?.[0];
                                  
                                  return (
                                    <>
                                      {/* V (mL) - Initial Volume */}
                                      <TableCell align="center" sx={{ backgroundColor: '#fff3e0' }}>
                                        <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 'bold', textAlign: 'center' }}>
                                          {prepData?.initial_volume?.toFixed(1) || prepData?.initialVolume?.toFixed(1) || 'N/A'}
                                        </Typography>
                                      </TableCell>
                                      
                                      {/* p (g) - Initial Weight */}
                                      <TableCell align="center" sx={{ backgroundColor: '#fff3e0' }}>
                                        <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 'bold', textAlign: 'center' }}>
                                          {prepData?.initial_weight?.toFixed(2) || prepData?.initialWeight?.toFixed(2) || 'N/A'}
                                        </Typography>
                                      </TableCell>
                                      
                                      {/* vf (mL) - Final Volume */}
                                      <TableCell align="center" sx={{ backgroundColor: '#fff3e0' }}>
                                        <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 'bold', textAlign: 'center' }}>
                                          {dilution?.finalVolume?.toFixed(1) || 'N/A'}
                                        </Typography>
                                      </TableCell>
                                      
                                      {/* vp (mL) - Volume Taken */}
                                      <TableCell align="center" sx={{ backgroundColor: '#fff3e0' }}>
                                        <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 'bold', textAlign: 'center' }}>
                                          {dilution?.volumeTaken?.toFixed(1) || 'N/A'}
                                        </Typography>
                                      </TableCell>
                                      
                                      {/* NEW: vf1 (mL) - Alternative Final Volume */}
                                      <TableCell align="center" sx={{ backgroundColor: '#f3e5f5' }}>
                                        <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 'bold', textAlign: 'center' }}>
                                          {(() => {
                                            // Try analysis form first, fallback to original preparation data
                                            const vf1 = prepData?.vf1 || sample.metadonnees?.saa_preparation?.vf1;
                                            return vf1?.toFixed(1) || 'N/A';
                                          })()}
                                        </Typography>
                                      </TableCell>
                                      
                                      {/* NEW: vp1 (mL) - Alternative Volume Taken */}
                                      <TableCell align="center" sx={{ backgroundColor: '#f3e5f5' }}>
                                        <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 'bold', textAlign: 'center' }}>
                                          {(() => {
                                            // Try analysis form first, fallback to original preparation data
                                            const vp1 = prepData?.vp1 || sample.metadonnees?.saa_preparation?.vp1;
                                            return vp1?.toFixed(1) || 'N/A';
                                          })()}
                                        </Typography>
                                      </TableCell>
                                      

                                    </>
                                  );
                                })()}
                                  
                                {/* Analysis Results - Fixed Data Extraction */}
                                  {(() => {
                                    // ✅ CRITICAL FIX: Use consistent element ordering to prevent column misalignment
                                    const elementsToShow = getSAABatchElementOrder((selectedSample as any)?.batchSamples || [selectedSample]);
                                    
                                    // ✅ FIXED: Get analysis results from the correct location
                                    const saaAnalysisReadings = sample.metadonnees?.saa_analysis_form?.analysis_readings || [];
                                    const generalAnalysisResults = sample.metadonnees?.analysis_results || [];
                                    
                                    // Use SAA analysis readings first, fallback to general analysis results
                                    const analysisData = saaAnalysisReadings.length > 0 ? saaAnalysisReadings : generalAnalysisResults;

                                      // ✅ ENHANCED: Get method code from analyses_demandees
                                      const saaMethodCode = sample.metadonnees?.analyses_demandees?.find((method: string) => 
                                        method.startsWith('AA-PRE.MO/ANA')
                                      ) || 'AA-PRE.MO/ANA/002'; // Default to multi-element SAA
                                      
                                      // ✅ METHOD-SPECIFIC DETECTION LIMITS
                                      // Import from backend configuration - using hardcoded values for frontend
                                      const getMethodSpecificLimit = (element: string, methodCode: string) => {
                                        // SAA Method Limits
                                        // 🔬 Ag and Au are displayed in g/t, all other elements in %
                                        // Detection limits converted: ppm / 10000 = % (e.g., 1 ppm = 0.0001%)
                                        const saaLimits: Record<string, { lod: number; loq: number; unit: string }> = {
                                          'Ag': { lod: 1, loq: 1, unit: 'g/t' },      // Keep g/t for silver
                                          'Au': { lod: 0.01, loq: 0.01, unit: 'g/t' }, // Keep g/t for gold
                                          'Cd': { lod: 0.00005, loq: 0.00005, unit: '%' }, // 0.5 ppm → 0.00005%
                                          'Co': { lod: 0.0001, loq: 0.0001, unit: '%' },   // 1 ppm → 0.0001%
                                          'Cu': { lod: 0.0001, loq: 0.0001, unit: '%' },   // 1 ppm → 0.0001%
                                          'Fe': { lod: 0.0001, loq: 0.0001, unit: '%' },   // 1 ppm → 0.0001%
                                          'Mn': { lod: 0.0005, loq: 0.0005, unit: '%' },   // 5 ppm → 0.0005%
                                          'Ni': { lod: 0.0001, loq: 0.0001, unit: '%' },   // 1 ppm → 0.0001%
                                          'Pb': { lod: 0.0001, loq: 0.0001, unit: '%' },   // 1 ppm → 0.0001%
                                          'Zn': { lod: 0.0001, loq: 0.0001, unit: '%' }    // 1 ppm → 0.0001%
                                        };
                                        
                                        // For SAA methods, use SAA-specific limits
                                        if (methodCode.startsWith('AA-PRE.MO/ANA')) {
                                          return saaLimits[element] || { lod: 0.0001, loq: 0.0001, unit: '%' };
                                        }
                                        
                                        // Default fallback (shouldn't happen for SAA dashboard)
                                        return { lod: 0.0001, loq: 0.0001, unit: '%' };
                                      };
                                      
                                      // Build detection limits for each element based on method
                                      const detectionLimits: Record<string, { lod: number; loq: number; unit: string }> = {};
                                      elementsToShow.forEach((element: string) => {
                                        detectionLimits[element] = getMethodSpecificLimit(element, saaMethodCode);
                                      });
                                      
                                  // 🔧 FIX: Get this sample's specific elements for greyed field detection
                                  const sampleSpecificElems = getSampleSpecificElements(sample);
                                  
                                  return elementsToShow.flatMap((element: string) => {
                                    const hasElement = sampleSpecificElems.includes(element);
                                    
                                    // 🔧 FIX: Show greyed cells for elements not in this sample
                                    if (!hasElement) {
                                      return [
                                        <TableCell key={`${element}-lecture`} align="center" sx={{ 
                                          backgroundColor: '#f5f5f5',
                                          borderLeft: '2px solid #4caf50',
                                          borderRight: '1px solid #4caf50'
                                        }}>
                                          <Typography sx={{ color: '#bdbdbd', fontSize: '0.7rem' }}>-</Typography>
                                        </TableCell>,
                                        <TableCell key={`${element}-dilution`} align="center" sx={{ 
                                          backgroundColor: '#f5f5f5',
                                          borderRight: '1px solid #2196f3'
                                        }}>
                                          <Typography sx={{ color: '#bdbdbd', fontSize: '0.7rem' }}>-</Typography>
                                        </TableCell>,
                                        <TableCell key={`${element}-choice`} align="center" sx={{ 
                                          backgroundColor: '#f5f5f5',
                                          borderRight: '1px solid #9c27b0'
                                        }}>
                                          <Typography sx={{ color: '#bdbdbd', fontSize: '0.7rem' }}>-</Typography>
                                        </TableCell>,
                                        <TableCell key={`${element}-concentration`} align="center" sx={{ 
                                          backgroundColor: '#f5f5f5'
                                        }}>
                                          <Typography sx={{ color: '#bdbdbd', fontSize: '0.7rem' }}>-</Typography>
                                        </TableCell>,
                                        <TableCell key={`${element}-corrected`} align="center" sx={{ 
                                          backgroundColor: '#f5f5f5',
                                          borderRight: '2px solid #e91e63'
                                        }}>
                                          <Typography sx={{ color: '#bdbdbd', fontSize: '0.7rem' }}>-</Typography>
                                        </TableCell>
                                      ];
                                    }
                                    
                                    const result = analysisData.find((r: any) => r.element === element);
                                      const limits = detectionLimits[element];
                                    const concentration = result?.concentration_ppm || 0;
                                    const finalConcentration = result?.concentration_final_percent || (result?.concentration_percent) || 0;
                                      
                                    return [
                                      // Raw lecture (reading from CSV import)
                                      <TableCell key={`${element}-lecture`} align="center" sx={{ 
                                        backgroundColor: '#e8f5e8',
                                        borderLeft: '2px solid #4caf50',
                                        borderRight: '1px solid #4caf50',
                                        fontWeight: 'bold'
                                      }}>
                                        <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>
                                          {(() => {
                                            // 🧮 REVERSE CALCULATION: Calculate original lecture from concentration_ppm
                                            // We have: ppm = L × (V/p) × (vf/vp) [for choice 1]
                                            // Therefore: L = ppm / ((V/p) × (vf/vp))
                                            const batchAnalysisSummary = sample.metadonnees?.batch_analysis_summary;
                                            
                                            const concentrationPpm = result?.concentration_ppm || 0;
                                            
                                            // ✅ SIMPLE SOLUTION: Use stored absorbance field (which contains original Reading/lecture)
                                            const originalLecture = result?.absorbance || 0;
                                            
                                            return originalLecture > 0 ? originalLecture.toFixed(4) : 'N/A';
                                          })()}
                                            </Typography>
                                      </TableCell>,
                                      
                                      // Dilution factor
                                      <TableCell key={`${element}-dilution`} align="center" sx={{ 
                                        backgroundColor: '#e3f2fd',
                                        borderRight: '1px solid #2196f3'
                                      }}>
                                        <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>
                                          {(() => {
                                            // ✅ FIXED: Correct path for SAA dilution factor with multiple property name variations
                                            const dilutionFactor = sample.metadonnees?.saa_preparation?.total_dilution_factor ||
                                                                 sample.metadonnees?.saa_preparation?.totalDilutionFactor ||
                                                                 sample.metadonnees?.saa_preparation?.dilution_factor ||
                                                                 sample.metadonnees?.saa_preparation?.dilutionFactor ||
                                                                 sample.metadonnees?.saa_analysis_form?.preparation_data?.total_dilution_factor ||
                                                                 sample.metadonnees?.saa_analysis_form?.preparation_data?.totalDilutionFactor ||
                                                                 sample.metadonnees?.preparation_data?.total_dilution_factor;

                                            
                                            return dilutionFactor ? `${dilutionFactor}x` : 'N/A';
                                          })()}
                                            </Typography>
                                      </TableCell>,
                                      
                                      // ✅ NEW: Individual Element Dilution Choice
                                      <TableCell key={`${element}-choice`} align="center" sx={{ 
                                        backgroundColor: '#f3e5f5',
                                        borderRight: '1px solid #9c27b0'
                                      }}>
                                        <Box sx={{ textAlign: 'center' }}>
                                          {(() => {
                                            const batchAnalysisSummary = sample.metadonnees?.batch_analysis_summary;
                                            const elementDilutionChoices = batchAnalysisSummary?.element_dilution_choices || {};
                                            const elementDilutionChoice = elementDilutionChoices[element] ?? 1;
                                            
                                            return (
                                              <>
                                                <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}>
                                                  {elementDilutionChoice}
                                                </Typography>
                                                <Typography variant="caption" sx={{ fontSize: '0.55rem', display: 'block' }}>
                                                  {elementDilutionChoice === 0 ? 'L×(V/p)' : 
                                                   elementDilutionChoice === 2 ? 'L×(V/p)×(vf1/vp1)' : 'L×(V/p)×(vf/vp)'}
                                                </Typography>
                                              </>
                                            );
                                          })()}
                                        </Box>
                                      </TableCell>,
                                      
                                      // Final Concentration - With golden rule and detection limits
                                      <TableCell key={`${element}-concentration`} align="center" sx={{ 
                                        backgroundColor: '#fff3e0'
                                      }}>
                                        {(() => {
                                          if (!limits) {
                                            return <Chip label="N/A" color="default" sx={{ fontSize: '0.7rem', height: '18px' }} />;
                                          }
                                          
                                          // 🔬 UNIT CONVERSION: Convert ppm to target unit for display
                                          // concentration is always stored in ppm internally
                                          let displayValue = concentration;
                                          const displayUnit = limits.unit;
                                          const displayLod = limits.lod;
                                          
                                          // Convert ppm to % if unit is % (divide by 10000)
                                          if (displayUnit === '%') {
                                            displayValue = concentration / 10000;
                                          }
                                          // g/t is same as ppm, no conversion needed
                                          
                                          // Check for zero/negative values
                                          if (concentration <= 0) {
                                            const cellKey = `${sample.id}-${element}`;
                                            if (editingCell === cellKey) {
                                              return (
                                                <TextField
                                                  size="small"
                                                  autoFocus
                                                  value={editCellValue}
                                                  onChange={(e) => setEditCellValue(e.target.value)}
                                                  onKeyDown={(e) => {
                                                    if (e.key === 'Enter') saveConcentrationEdit(sample.id, element, editCellValue, displayUnit);
                                                    if (e.key === 'Escape') setEditingCell(null);
                                                  }}
                                                  onBlur={() => saveConcentrationEdit(sample.id, element, editCellValue, displayUnit)}
                                                  InputProps={{ sx: { fontSize: '0.7rem', height: '24px', width: '80px' } }}
                                                />
                                              );
                                            }
                                            return <Chip label={`< ${displayLod} ${displayUnit}`} color="error" sx={{ fontSize: '0.7rem', height: '18px', cursor: 'pointer' }} onClick={() => { setEditingCell(cellKey); setEditCellValue(''); }} />;
                                          }
                                          
                                          // Check if below detection limit
                                          if (displayValue < displayLod) {
                                            const cellKey = `${sample.id}-${element}`;
                                            if (editingCell === cellKey) {
                                              return (
                                                <TextField
                                                  size="small"
                                                  autoFocus
                                                  value={editCellValue}
                                                  onChange={(e) => setEditCellValue(e.target.value)}
                                                  onKeyDown={(e) => {
                                                    if (e.key === 'Enter') saveConcentrationEdit(sample.id, element, editCellValue, displayUnit);
                                                    if (e.key === 'Escape') setEditingCell(null);
                                                  }}
                                                  onBlur={() => saveConcentrationEdit(sample.id, element, editCellValue, displayUnit)}
                                                  InputProps={{ sx: { fontSize: '0.7rem', height: '24px', width: '80px' } }}
                                                />
                                              );
                                            }
                                            return <Chip label={`< ${displayLod} ${displayUnit}`} color="warning" sx={{ fontSize: '0.7rem', height: '18px', cursor: 'pointer' }} onClick={() => { setEditingCell(cellKey); setEditCellValue(''); }} />;
                                          }
                                          
                                          // Format the value based on unit
                                          let formattedValue;
                                          if (displayUnit === '%') {
                                            formattedValue = displayValue.toFixed(2);
                                          } else if (displayUnit === 'g/t') {
                                            formattedValue = displayValue.toFixed(2);
                                          } else {
                                            formattedValue = displayValue.toFixed(0);
                                          }
                                          const cellKey = `${sample.id}-${element}`;
                                          if (editingCell === cellKey) {
                                            return (
                                              <TextField
                                                size="small"
                                                autoFocus
                                                value={editCellValue}
                                                onChange={(e) => setEditCellValue(e.target.value)}
                                                onKeyDown={(e) => {
                                                  if (e.key === 'Enter') saveConcentrationEdit(sample.id, element, editCellValue, displayUnit);
                                                  if (e.key === 'Escape') setEditingCell(null);
                                                }}
                                                onBlur={() => saveConcentrationEdit(sample.id, element, editCellValue, displayUnit)}
                                                InputProps={{ sx: { fontSize: '0.7rem', height: '24px', width: '80px' } }}
                                              />
                                            );
                                          }
                                          return (
                                            <Chip
                                              label={`${formattedValue} ${displayUnit}`}
                                              color="success"
                                              sx={{ fontSize: '0.7rem', height: '18px', fontWeight: 'bold', cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
                                              onClick={() => { setEditingCell(cellKey); setEditCellValue(formattedValue); }}
                                            />
                                          );
                                        })()}
                                      </TableCell>,
                                      
                                      // 🔬 NEW: Corrected Value Column
                                      <TableCell key={`${element}-corrected`} align="center" sx={{ 
                                        backgroundColor: '#fce4ec',
                                        borderRight: '2px solid #e91e63'
                                      }}>
                                        {(() => {
                                          // Check for corrected value from multiple sources:
                                          // 1. Frontend state (after clicking "Corriger")
                                          // 2. Database (sample.metadonnees.saa_analysis.elements)
                                          const frontendCorrected = correctedValues[sample.id]?.[element];
                                          const dbCorrected = sample.metadonnees?.saa_analysis?.elements?.[element];
                                          
                                          const hasCorrectedValue = frontendCorrected || dbCorrected?.correction_applied;
                                          const correctedPpm = frontendCorrected || dbCorrected?.corrected_ppm;
                                          
                                          if (!hasCorrectedValue || !correctedPpm) {
                                            return <Typography sx={{ color: '#bdbdbd', fontSize: '0.7rem' }}>-</Typography>;
                                          }
                                          
                                          // Apply same unit conversion logic as concentration
                                          let displayValue = correctedPpm;
                                          const displayUnit = limits?.unit || '%';
                                          
                                          // Convert ppm to % if unit is % (divide by 10000)
                                          if (displayUnit === '%') {
                                            displayValue = correctedPpm / 10000;
                                          }
                                          // g/t is same as ppm, no conversion needed
                                          
                                          // Format the value
                                          let formattedValue;
                                          if (displayUnit === '%') {
                                            formattedValue = displayValue.toFixed(2);
                                          } else if (displayUnit === 'g/t') {
                                            formattedValue = displayValue.toFixed(2);
                                          } else {
                                            formattedValue = displayValue.toFixed(0);
                                          }
                                          
                                          return (
                                            <Chip 
                                              label={`${formattedValue} ${displayUnit}`} 
                                              color="secondary" 
                                              sx={{ 
                                                fontSize: '0.7rem', 
                                                height: '18px', 
                                                fontWeight: 'bold',
                                                backgroundColor: '#e91e63',
                                                color: 'white'
                                              }} 
                                            />
                                          );
                                        })()}
                                      </TableCell>
                                    ];
                                    });
                                  })()}
                                </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>
                  
                  {/* ===================== QA/QC REFERENCE TABLE SECTION ===================== */}
                  <Card sx={{ mt: 2, backgroundColor: alpha('#9c27b0', 0.04), border: `2px solid ${alpha('#9c27b0', 0.3)}` }}>
                    <CardContent sx={{ p: 2 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 'bold', color: '#9c27b0' }}>
                          📊 QA/QC - Tableau de Référence (BIS, Blancs, CRM)
                        </Typography>
                        {!showQaqcForm ? (
                          <Button
                            variant="contained"
                            size="small"
                            onClick={loadDefaultSAAQAQCTemplate}
                            sx={{ backgroundColor: '#9c27b0', '&:hover': { backgroundColor: '#7b1fa2' } }}
                          >
                            Charger Template QA/QC
                          </Button>
                        ) : (
                          <Button
                            variant="outlined"
                            size="small"
                            color="error"
                            onClick={() => { setShowQaqcForm(false); setQaqcStandards([]); setFirstBISSampleId(null); setSecondBISSampleId(null); setThirdBISSampleId(null); setFourthBISSampleId(null); }}
                          >
                            Fermer QA/QC
                          </Button>
                        )}
                      </Box>

                      {showQaqcForm && qaqcStandards.length > 0 && (() => {
                        const qaqcElements = getQAQCElements();
                        const limits = getSAADetectionLimits();
                        return (
                          <>
                            <Alert severity="success" sx={{ mb: 2, fontSize: '0.75rem' }}>
                              ✅ Template chargé ({qaqcElements.length} éléments: {qaqcElements.join(', ')}). Sélectionnez les BIS et remplissez les valeurs.
                            </Alert>

                            {/* BIS SELECTION */}
                            <Box sx={{ mb: 3, p: 2, backgroundColor: alpha('#2196f3', 0.08), border: `2px solid ${theme.palette.info.main}`, borderRadius: 1 }}>
                              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: '#1976d2' }}>
                                📊 Sélectionner le 1er BIS (ligne 1) :
                              </Typography>
                              <Box sx={{ maxHeight: 200, overflow: 'auto', mb: 2 }}>
                                <Grid container spacing={1}>
                                  {((selectedSample as any)?.batchSamples || []).map((sample: any, idx: number) => (
                                    <Grid item xs={12} sm={6} md={4} key={sample.id}>
                                      <Paper sx={{ p: 1, backgroundColor: '#fff', display: 'flex', alignItems: 'center' }}>
                                        <FormControlLabel
                                          control={
                                            <Checkbox
                                              checked={sample.id === firstBISSampleId}
                                              onChange={(e) => handleFirstSAABISChange(sample.id, e.target.checked)}
                                              disabled={sample.id === secondBISSampleId || sample.id === thirdBISSampleId || sample.id === fourthBISSampleId}
                                              size="small"
                                              sx={{ color: '#1976d2', '&.Mui-checked': { color: '#1976d2' } }}
                                            />
                                          }
                                          label={
                                            <Box>
                                              <Typography sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>{sample.sample_id}</Typography>
                                              <Typography sx={{ fontSize: '0.65rem', color: '#666' }}>
                                                {sample.external_id || 'N/A'} - Pos {idx + 1}
                                              </Typography>
                                            </Box>
                                          }
                                        />
                                        {sample.id === firstBISSampleId && (
                                          <Chip label="1er BIS" size="small" color="primary" sx={{ ml: 'auto', fontSize: '0.6rem' }} />
                                        )}
                                      </Paper>
                                    </Grid>
                                  ))}
                                </Grid>
                              </Box>

                              <Divider sx={{ my: 2 }} />

                              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: '#ff9800' }}>
                                📊 Sélectionner le 2ème BIS (ligne 2) :
                              </Typography>
                              <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                                <Grid container spacing={1}>
                                  {((selectedSample as any)?.batchSamples || []).map((sample: any, idx: number) => (
                                    <Grid item xs={12} sm={6} md={4} key={`second-${sample.id}`}>
                                      <Paper sx={{ p: 1, backgroundColor: '#fff', display: 'flex', alignItems: 'center' }}>
                                        <FormControlLabel
                                          control={
                                            <Checkbox
                                              checked={sample.id === secondBISSampleId}
                                              onChange={(e) => handleSecondSAABISChange(sample.id, e.target.checked)}
                                              disabled={sample.id === firstBISSampleId || sample.id === thirdBISSampleId || sample.id === fourthBISSampleId}
                                              size="small"
                                              sx={{ color: '#ff9800', '&.Mui-checked': { color: '#ff9800' } }}
                                            />
                                          }
                                          label={
                                            <Box>
                                              <Typography sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>{sample.sample_id}</Typography>
                                              <Typography sx={{ fontSize: '0.65rem', color: '#666' }}>
                                                {sample.external_id || 'N/A'} - Pos {idx + 1}
                                              </Typography>
                                            </Box>
                                          }
                                        />
                                        {sample.id === secondBISSampleId && (
                                          <Chip label="2ème BIS" size="small" color="warning" sx={{ ml: 'auto', fontSize: '0.6rem' }} />
                                        )}
                                      </Paper>
                                    </Grid>
                                  ))}
                                </Grid>
                              </Box>

                              <Divider sx={{ my: 2 }} />

                              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: '#4caf50' }}>
                                📊 Sélectionner le 3ème BIS (ligne 3) :
                              </Typography>
                              <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                                <Grid container spacing={1}>
                                  {((selectedSample as any)?.batchSamples || []).map((sample: any, idx: number) => (
                                    <Grid item xs={12} sm={6} md={4} key={`third-${sample.id}`}>
                                      <Paper sx={{ p: 1, backgroundColor: '#fff', display: 'flex', alignItems: 'center' }}>
                                        <FormControlLabel
                                          control={
                                            <Checkbox
                                              checked={sample.id === thirdBISSampleId}
                                              onChange={(e) => handleThirdSAABISChange(sample.id, e.target.checked)}
                                              disabled={sample.id === firstBISSampleId || sample.id === secondBISSampleId || sample.id === fourthBISSampleId}
                                              size="small"
                                              sx={{ color: '#4caf50', '&.Mui-checked': { color: '#4caf50' } }}
                                            />
                                          }
                                          label={
                                            <Box>
                                              <Typography sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>{sample.sample_id}</Typography>
                                              <Typography sx={{ fontSize: '0.65rem', color: '#666' }}>
                                                {sample.external_id || 'N/A'} - Pos {idx + 1}
                                              </Typography>
                                            </Box>
                                          }
                                        />
                                        {sample.id === thirdBISSampleId && (
                                          <Chip label="3ème BIS" size="small" sx={{ ml: 'auto', fontSize: '0.6rem', backgroundColor: '#4caf50', color: 'white' }} />
                                        )}
                                      </Paper>
                                    </Grid>
                                  ))}
                                </Grid>
                              </Box>

                              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 'bold', color: '#ff5722' }}>
                                📊 Sélectionner le 4ème BIS (ligne 4) :
                              </Typography>
                              <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                                <Grid container spacing={1}>
                                  {((selectedSample as any)?.batchSamples || []).map((sample: any, idx: number) => (
                                    <Grid item xs={12} sm={6} md={4} key={`fourth-${sample.id}`}>
                                      <Paper sx={{ p: 1, backgroundColor: '#fff', display: 'flex', alignItems: 'center' }}>
                                        <FormControlLabel
                                          control={
                                            <Checkbox
                                              checked={sample.id === fourthBISSampleId}
                                              onChange={(e) => handleFourthSAABISChange(sample.id, e.target.checked)}
                                              disabled={sample.id === firstBISSampleId || sample.id === secondBISSampleId || sample.id === thirdBISSampleId}
                                              size="small"
                                              sx={{ color: '#ff5722', '&.Mui-checked': { color: '#ff5722' } }}
                                            />
                                          }
                                          label={
                                            <Box>
                                              <Typography sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>{sample.sample_id}</Typography>
                                              <Typography sx={{ fontSize: '0.65rem', color: '#666' }}>
                                                {sample.external_id || 'N/A'} - Pos {idx + 1}
                                              </Typography>
                                            </Box>
                                          }
                                        />
                                        {sample.id === fourthBISSampleId && (
                                          <Chip label="4ème BIS" size="small" sx={{ ml: 'auto', fontSize: '0.6rem', backgroundColor: '#ff5722', color: 'white' }} />
                                        )}
                                      </Paper>
                                    </Grid>
                                  ))}
                                </Grid>
                              </Box>

                              <Alert severity="info" sx={{ mt: 2, fontSize: '0.7rem' }}>
                                💡 Chaque BIS sélectionné utilise sa propre référence client (auto-remplie depuis l'<em>external_id</em> de l'échantillon).
                              </Alert>
                            </Box>

                            {/* EDITABLE QA/QC TABLE */}
                            <TableContainer component={Paper} sx={{ maxHeight: 500, overflow: 'auto' }}>
                              <Table size="small" stickyHeader>
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.7rem', textAlign: 'center', border: '1px solid #555', minWidth: '140px' }}>
                                      Réf. Client
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.7rem', textAlign: 'center', border: '1px solid #555', minWidth: '110px' }}>
                                      Réf. Labo
                                    </TableCell>
                                    {qaqcElements.map(el => (
                                      <TableCell key={el} sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.7rem', textAlign: 'center', border: '1px solid #555', minWidth: '70px' }}>
                                        {el}<br/>
                                        <span style={{ fontSize: '0.55rem', color: '#555' }}>
                                          LD: {limits[el] || '?'} | {el === 'Ag' ? 'g/t' : '%'}
                                        </span>
                                      </TableCell>
                                    ))}
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {qaqcStandards.map((standard, idx) => {
                                    const isBISRow = idx <= 3;
                                    const isBlankRow = idx === 4 || idx === 5;
                                    const isCRMRow = idx >= 6;
                                    const rowColor = isBISRow ? '#e3f2fd' : isBlankRow ? '#fff3e0' : isCRMRow ? '#f3e5f5' : '#fff';
                                    return (
                                      <TableRow key={idx} sx={{ backgroundColor: rowColor }}>
                                        <TableCell sx={{ border: '1px solid #ddd', p: 0.5 }}>
                                          <TextField
                                            value={standard.customers_ref}
                                            onChange={(e) => updateQAQCStandardField(idx, 'customers_ref', e.target.value)}
                                            size="small"
                                            fullWidth
                                            variant="standard"
                                            InputProps={{ sx: { fontSize: '0.7rem' } }}
                                          />
                                        </TableCell>
                                        <TableCell sx={{ border: '1px solid #ddd', p: 0.5 }}>
                                          <TextField
                                            value={standard.lab_ref}
                                            onChange={(e) => updateQAQCStandardField(idx, 'lab_ref', e.target.value)}
                                            size="small"
                                            fullWidth
                                            variant="standard"
                                            InputProps={{ sx: { fontSize: '0.7rem' } }}
                                          />
                                        </TableCell>
                                        {qaqcElements.map(el => (
                                          <TableCell key={el} sx={{ border: '1px solid #ddd', p: 0.5 }}>
                                            <TextField
                                              value={standard.elements[el] || ''}
                                              onChange={(e) => updateQAQCStandardField(idx, el, e.target.value)}
                                              size="small"
                                              fullWidth
                                              variant="standard"
                                              InputProps={{ sx: { fontSize: '0.7rem', textAlign: 'center' } }}
                                            />
                                          </TableCell>
                                        ))}
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </TableContainer>

                            <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              <Chip label="Lignes 1-4: BIS" size="small" sx={{ backgroundColor: '#e3f2fd', fontSize: '0.65rem' }} />
                              <Chip label="Lignes 5-6: Blancs" size="small" sx={{ backgroundColor: '#fff3e0', fontSize: '0.65rem' }} />
                              <Chip label="Lignes 7+: CRM Standards" size="small" sx={{ backgroundColor: '#f3e5f5', fontSize: '0.65rem' }} />
                            </Box>
                          </>
                        );
                      })()}
                    </CardContent>
                  </Card>

                  {/* Batch Validation Decision Section */}
                  <Card sx={{ mt: 2, backgroundColor: '#f0f8ff' }}>
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem', fontWeight: 'bold', color: '#1976d2' }}>
                        📝 Décision de Validation du Lot
                      </Typography>
                      
                      {/* Validation Decision Radio Buttons */}
                      <FormControl component="fieldset" sx={{ mb: 2 }}>
                        <RadioGroup
                          value={validationDecision}
                          onChange={(e) => setValidationDecision(e.target.value as 'APPROVE' | 'REJECT' | 'REQUEST_CORRECTIONS')}
                          row
                        >
                          <FormControlLabel
                            value="APPROVE"
                            control={<Radio color="success" />} 
                            label={
                              <Box display="flex" alignItems="center" gap={1}>
                                <CheckCircle color="success" sx={{ fontSize: '1rem' }} />
                                <Typography variant="body2">Approuver le Lot</Typography>
                              </Box>
                            }
                          />
                          <FormControlLabel
                            value="REJECT"
                            control={<Radio color="error" />} 
                            label={
                              <Box display="flex" alignItems="center" gap={1}>
                                <Cancel color="error" sx={{ fontSize: '1rem' }} />
                                <Typography variant="body2">Rejeter le Lot</Typography>
                              </Box>
                            }
                          />
                          <FormControlLabel
                            value="REQUEST_CORRECTIONS" 
                            control={<Radio color="warning" />} 
                            label={
                              <Box display="flex" alignItems="center" gap={1}>
                                <Edit color="warning" sx={{ fontSize: '1rem' }} />
                                <Typography variant="body2">Demander Corrections</Typography>
                              </Box>
                            }
                          />
                        </RadioGroup>
                      </FormControl>
                      
                      {/* Validation Notes */}
                      <TextField
                        label="Notes de Validation du Lot"
                        placeholder="Saisissez vos commentaires et observations de validation pour ce lot..."
                        multiline
                        rows={4}
                        fullWidth
                        value={validationNotes}
                        onChange={(e) => setValidationNotes(e.target.value)}
                        error={!validationNotes.trim()}
                        helperText={
                          !validationNotes.trim() 
                            ? "Les notes de validation sont obligatoires pour approuver ou rejeter un lot" 
                            : `${validationNotes.length}/500 caractères`
                        }
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            fontSize: '0.9rem',
                            backgroundColor: 'white'
                          }
                        }}
                      />
                    </CardContent>
                  </Card>
                </Box>
              ) : selectedSample.metadonnees?.saa_analysis_form ? (
                /* Individual Sample Validation View - Enhanced with Complete Information */
                <Box>
                  {/* Comprehensive Sample Information Section */}
                  <Card sx={{ mb: 3, backgroundColor: '#e8f5e8', border: '2px solid #4caf50' }}>
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="h6" sx={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#2e7d32', mb: 2 }}>
                        📋 Informations Complètes de l'Échantillon {selectedSample.sample_id}
                      </Typography>
                      
                      <Grid container spacing={2}>
                        {/* Basic Sample Info */}
                        <Grid item xs={12} sm={6} md={3}>
                          <Card variant="outlined" sx={{ p: 1, backgroundColor: 'white' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Client</Typography>
                            <Typography variant="body1" sx={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                              {selectedSample.client?.company_name || 'N/A'}
                            </Typography>
                          </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <Card variant="outlined" sx={{ p: 1, backgroundColor: 'white' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Référence Client</Typography>
                            <Typography variant="body1" sx={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                              {selectedSample.external_id || 'Non spécifiée'}
                            </Typography>
                          </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <Card variant="outlined" sx={{ p: 1, backgroundColor: 'white' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Type Échantillon</Typography>
                            <Typography variant="body1" sx={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                              {selectedSample.type_echantillon || 'N/A'}
                            </Typography>
                          </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <Card variant="outlined" sx={{ p: 1, backgroundColor: 'white' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Priorité</Typography>
                            <Chip 
                              label={selectedSample.priorite || 'NORMALE'} 
                              color={getPriorityColor(selectedSample.priorite || 'NORMALE')} 
                              size="small" 
                              sx={{ fontWeight: 'bold' }}
                            />
                          </Card>
                        </Grid>
                        
                        {/* Project and Site Info */}
                        <Grid item xs={12} sm={6}>
                          <Card variant="outlined" sx={{ p: 1, backgroundColor: 'white' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Projet / Site</Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                              <strong>Projet:</strong> {selectedSample.nom_projet || 'Non spécifié'}<br/>
                              <strong>Site Minier:</strong> {selectedSample.site_minier || 'Non spécifié'}
                            </Typography>
                          </Card>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Card variant="outlined" sx={{ p: 1, backgroundColor: 'white' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Dates Importantes</Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                              <strong>Réception:</strong> {new Date(selectedSample.date_reception).toLocaleDateString('fr-FR')}<br/>
                              <strong>Analyse SAA:</strong> {selectedSample.metadonnees?.saa_analysis_completed_at 
                                ? new Date(selectedSample.metadonnees.saa_analysis_completed_at).toLocaleDateString('fr-FR')
                                : 'N/A'
                              }
                            </Typography>
                          </Card>
                        </Grid>
                        
                        {/* Physical Properties */}
                        {selectedSample.metadonnees && (
                          <Grid item xs={12}>
                            <Card variant="outlined" sx={{ p: 1, backgroundColor: 'white' }}>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 1 }}>
                                Propriétés Physiques et Métadonnées
                              </Typography>
                              <Grid container spacing={1}>
                                <Grid item xs={6} sm={3}>
                                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                    <strong>Poids:</strong> {selectedSample.metadonnees.poids_kg || 'N/A'} kg
                                  </Typography>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                    <strong>Sacs:</strong> {selectedSample.metadonnees.nombre_sacs || 'N/A'}
                                  </Typography>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                    <strong>État:</strong> {selectedSample.metadonnees.etat_echantillon || 'N/A'}
                                  </Typography>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                    <strong>Température:</strong> {selectedSample.metadonnees.temperature_reception || 'N/A'}°C
                                  </Typography>
                                </Grid>
                                {selectedSample.metadonnees.description_physique && (
                                  <Grid item xs={12}>
                                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                      <strong>Description:</strong> {selectedSample.metadonnees.description_physique}
                                    </Typography>
                                  </Grid>
                                )}
                              </Grid>
                            </Card>
                          </Grid>
                        )}
                        
                        {/* Analysis Methods */}
                        <Grid item xs={12}>
                          <Card variant="outlined" sx={{ p: 1, backgroundColor: 'white' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 1 }}>
                              Méthodes d'Analyse Demandées
                            </Typography>
                            <Box display="flex" gap={1} flexWrap="wrap">
                              {(selectedSample.metadonnees?.analyses_demandees || selectedSample.methodes_demandees || ['SAA']).map((method: string, index: number) => (
                                <Chip 
                                  key={index} 
                                  label={method} 
                                  size="small"
                                  color="info" 
                                  variant="outlined"
                                  sx={{ fontSize: '0.75rem' }}
                                />
                              ))}
                            </Box>
                          </Card>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>

                  {/* Sample Analysis Results */}
                  <Card sx={{ mb: 3 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Résultats d'Analyse SAA</Typography>
                      
                          {/* Detection Limits Alert */}
                          {(() => {
                            const detectionLimits: Record<string, { lod: number; loq: number }> = {
                              'Fe': { lod: 0.100, loq: 0.300 },
                              'Cu': { lod: 0.050, loq: 0.150 },
                              'Pb': { lod: 0.080, loq: 0.250 },
                              'Zn': { lod: 0.030, loq: 0.100 },
                              'Mn': { lod: 0.020, loq: 0.060 },
                              'Ni': { lod: 0.040, loq: 0.120 },
                              'Cd': { lod: 0.010, loq: 0.030 },
                              'Ag': { lod: 0.005, loq: 0.015 }
                            };
                            
                            const analysisReadings = selectedSample.metadonnees.saa_analysis_form.analysis_readings || [];
                            const belowLODCount = analysisReadings.filter((reading: any) => {
                              const limits = detectionLimits[reading.element];
                              return limits && (reading.concentration_ppm || 0) < limits.lod;
                            }).length;
                            
                            const belowLOQCount = analysisReadings.filter((reading: any) => {
                              const limits = detectionLimits[reading.element];
                              const concentration = reading.concentration_ppm || 0;
                              return limits && concentration >= limits.lod && concentration < limits.loq;
                            }).length;
                            
                            if (belowLODCount > 0 || belowLOQCount > 0) {
                              return (
                                <Alert severity={belowLODCount > 0 ? "error" : "warning"} sx={{ mb: 3 }}>
                                  <Typography variant="subtitle2" gutterBottom>
                                    ⚠️ Alertes Limites de Détection
                                  </Typography>
                                  {belowLODCount > 0 && (
                                    <Typography variant="body2">
                                      • {belowLODCount} résultat(s) en dessous de la LOD (Limite de Détection) - Validation critique requise
                                    </Typography>
                                  )}
                                  {belowLOQCount > 0 && (
                                    <Typography variant="body2">
                                      • {belowLOQCount} résultat(s) entre LOD et LOQ (incertitude accrue) - Attention requise
                                    </Typography>
                                  )}
                                </Alert>
                              );
                            }
                            return null;
                          })()}

                          {/* Preparation Data */}
                          <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>Données de Préparation SAA</Typography>
                            
                            {/* Basic Preparation Data */}
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                              <Grid item xs={3}>
                                <Typography variant="body2">
                                  <strong>Poids initial (p):</strong> {selectedSample.metadonnees.saa_analysis_form.preparation_data?.initial_weight || 'N/A'} g
                                </Typography>
                              </Grid>
                              <Grid item xs={3}>
                                <Typography variant="body2">
                                  <strong>Méthode digestion:</strong> {selectedSample.metadonnees.saa_analysis_form.preparation_data?.digestion_method || 'N/A'}
                                </Typography>
                              </Grid>
                              <Grid item xs={3}>
                                <Typography variant="body2">
                                  <strong>Volume initial (V):</strong> {selectedSample.metadonnees.saa_analysis_form.preparation_data?.initial_volume || 'N/A'} mL
                                </Typography>
                              </Grid>
                              <Grid item xs={3}>
                                <Typography variant="body2">
                                  <strong>Facteur dilution total:</strong> {selectedSample.metadonnees?.saa_preparation?.total_dilution_factor || selectedSample.metadonnees?.saa_preparation?.totalDilutionFactor || selectedSample.metadonnees?.saa_preparation?.dilution_factor || selectedSample.metadonnees?.saa_preparation?.dilutionFactor || selectedSample.metadonnees?.saa_analysis_form?.preparation_data?.total_dilution_factor || 'N/A'}x
                                </Typography>
                              </Grid>
                            </Grid>

                            {/* Dilution Data (vf and vp) */}
                            {selectedSample.metadonnees.saa_analysis_form.preparation_data?.dilutions && 
                             selectedSample.metadonnees.saa_analysis_form.preparation_data.dilutions.length > 0 && (
                              <Box sx={{ mt: 2, p: 1.5, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ff9800' }}>
                                <Typography variant="body2" color="primary" fontWeight="bold" gutterBottom>
                                  Variables Formule SAA: L × (V/p) × (vf/vp)
                                </Typography>
                                <Grid container spacing={2}>
                                  {selectedSample.metadonnees.saa_analysis_form.preparation_data.dilutions.map((dilution: any, index: number) => (
                                    <React.Fragment key={index}>
                                      <Grid item xs={3}>
                                        <Typography variant="body2">
                                          <strong>Volume final (vf):</strong> {dilution.finalVolume?.toFixed(1) || 'N/A'} mL
                                        </Typography>
                                      </Grid>
                                      <Grid item xs={3}>
                                        <Typography variant="body2">
                                          <strong>Volume prélevé (vp):</strong> {dilution.volumeTaken?.toFixed(1) || 'N/A'} mL
                                        </Typography>
                                      </Grid>
                                      <Grid item xs={3}>
                                        <Typography variant="body2">
                                          <strong>Facteur dilution:</strong> {dilution.dilutionFactor?.toFixed(1) || 'N/A'}x
                                        </Typography>
                                      </Grid>
                                      <Grid item xs={3}>
                                        <Typography variant="body2" color="text.secondary">
                                          <strong>Dilution #{index + 1}</strong>
                                        </Typography>
                                      </Grid>
                                    </React.Fragment>
                                  ))}
                                </Grid>
                              </Box>
                            )}

                            {/* Formula Explanation */}
                            <Alert severity="info" sx={{ mt: 2 }}>
                              <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                                <strong>Formule SAA:</strong> Concentration = L × (V/p) × (vf/vp) 
                                <br />
                                <strong>Conversion:</strong> % = ppm ÷ 10,000
                              </Typography>
                            </Alert>
                          </Box>

                          {/* Analysis Results */}
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Élément</TableCell>
                                  <TableCell align="right">Longueur d'onde (nm)</TableCell>
                                  <TableCell align="right">Absorbance</TableCell>
                                  <TableCell align="right">Concentration (ppm)</TableCell>
                                  <TableCell align="right">Concentration finale</TableCell>
                                  <TableCell align="right">Incertitude</TableCell>
                                  <TableCell align="center">Statut QC</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {selectedSample.metadonnees.saa_analysis_form.analysis_readings?.map((reading: any) => (
                                  <TableRow key={`${selectedSample.id}-${reading.element}`}>
                                    <TableCell>
                                      <Chip label={reading.element} size="small" color="primary" />
                                    </TableCell>
                                    <TableCell align="right">
                                      {reading.instrument_settings?.wavelength || 'N/A'}
                                    </TableCell>
                                    <TableCell align="right">
                                      {reading.absorbance?.toFixed(4) || 'N/A'}
                                    </TableCell>
                                    <TableCell align="right">
                                      {reading.concentration_ppm?.toFixed(3) || 'N/A'}
                                    </TableCell>
                                    <TableCell align="right">
                                      {(() => {
                                        // Detection limits for proper display
                                        const detectionLimits: Record<string, { lod: number; loq: number }> = {
                                          'Fe': { lod: 0.100, loq: 0.300 },
                                          'Cu': { lod: 0.050, loq: 0.150 },
                                          'Pb': { lod: 0.080, loq: 0.250 },
                                          'Zn': { lod: 0.030, loq: 0.100 },
                                          'Mn': { lod: 0.020, loq: 0.060 },
                                          'Ni': { lod: 0.040, loq: 0.120 },
                                          'Cd': { lod: 0.010, loq: 0.030 },
                                          'Ag': { lod: 0.005, loq: 0.015 }
                                        };
                                        
                                        const limits = detectionLimits[reading.element];
                                        const concentration = reading.concentration_ppm || 0;
                                        const finalConcentration = reading.concentration_final_percent || 0;
                                        
                                        if (limits && concentration < limits.lod) {
                                          return (
                                            <Typography variant="body2" fontWeight={600} color="error">
                                              &lt; LOD
                                            </Typography>
                                          );
                                        } else if (limits && concentration < limits.loq) {
                                          return (
                                    <Typography variant="body2" fontWeight={600} color="warning.main" sx={{ fontSize: '0.65rem' }}>
                                              {reading.element === 'Fe' 
                                                ? `${finalConcentration.toFixed(4)}%`
                                        : `${(finalConcentration * 10000).toFixed(1)}`
                                              }
                                            </Typography>
                                          );
                                        } else {
                                          return (
                                    <Typography variant="body2" fontWeight={600} color="primary" sx={{ fontSize: '0.65rem' }}>
                                              {reading.element === 'Fe' 
                                                ? `${finalConcentration.toFixed(4)}%`
                                        : `${(finalConcentration * 10000).toFixed(1)}`
                                              }
                                            </Typography>
                                          );
                                        }
                                      })()}
                                    </TableCell>
                                    <TableCell align="right">
                                      ±{reading.uncertainty?.toFixed(3) || '0.000'}
                                    </TableCell>
                                    <TableCell align="center">
                                      {(() => {
                                        // Detection limits for SAA elements (from our quality standards)
                                        const detectionLimits: Record<string, { lod: number; loq: number }> = {
                                          'Fe': { lod: 0.100, loq: 0.300 },
                                          'Cu': { lod: 0.050, loq: 0.150 },
                                          'Pb': { lod: 0.080, loq: 0.250 },
                                          'Zn': { lod: 0.030, loq: 0.100 },
                                          'Mn': { lod: 0.020, loq: 0.060 },
                                          'Ni': { lod: 0.040, loq: 0.120 },
                                          'Cd': { lod: 0.010, loq: 0.030 },
                                          'Ag': { lod: 0.005, loq: 0.015 }
                                        };
                                        
                                        const limits = detectionLimits[reading.element];
                                        const concentration = reading.concentration_ppm || 0;
                                        
                                        if (!limits) {
                                          return <Chip label="N/A" color="default" size="small" />;
                                        }
                                        
                                        if (concentration < limits.lod) {
                                          return <Chip label="&lt; LOD" color="error" size="small" />;
                                        } else if (concentration < limits.loq) {
                                          return <Chip label="LOD-LOQ" color="warning" size="small" />;
                                        } else {
                                          return <Chip label="PASSED" color="success" size="small" />;
                                        }
                                      })()}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>

                          {/* Calibration Info */}
                          <Box sx={{ mt: 3 }}>
                            <Typography variant="subtitle2" gutterBottom>Information de Calibration</Typography>
                            <Typography variant="body2" color="text.secondary">
                              Coefficient R² : {selectedSample.metadonnees.saa_analysis_form.instrument_status?.r_squared?.toFixed(4) || '0.9995'} 
                              {(selectedSample.metadonnees.saa_analysis_form.instrument_status?.r_squared || 0.9995) >= 0.995 
                                ? ' ✓ Conforme' 
                                : ' ✗ Non conforme'
                              }
                            </Typography>
                          </Box>
                    </CardContent>
                  </Card>
                  
                  {/* Validation Decision Section */}
                  <Card sx={{ mt: 2, backgroundColor: '#f0f8ff' }}>
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem', fontWeight: 'bold', color: '#1976d2' }}>
                        📝 Décision de Validation
                    </Typography>
                    
                      {/* Validation Decision Radio Buttons */}
                      <FormControl component="fieldset" sx={{ mb: 2 }}>
                      <RadioGroup
                        value={validationDecision}
                          onChange={(e) => setValidationDecision(e.target.value as 'APPROVE' | 'REJECT' | 'REQUEST_CORRECTIONS')}
                          row
                      >
                        <FormControlLabel
                          value="APPROVE"
                            control={<Radio color="success" />} 
                          label={
                            <Box display="flex" alignItems="center" gap={1}>
                                <CheckCircle color="success" sx={{ fontSize: '1rem' }} />
                                <Typography variant="body2">Approuver</Typography>
                            </Box>
                          }
                        />
                        <FormControlLabel
                          value="REJECT"
                            control={<Radio color="error" />} 
                          label={
                            <Box display="flex" alignItems="center" gap={1}>
                                <Cancel color="error" sx={{ fontSize: '1rem' }} />
                                <Typography variant="body2">Rejeter</Typography>
                            </Box>
                          }
                        />
                        <FormControlLabel
                            value="REQUEST_CORRECTIONS" 
                            control={<Radio color="warning" />} 
                          label={
                            <Box display="flex" alignItems="center" gap={1}>
                                <Edit color="warning" sx={{ fontSize: '1rem' }} />
                                <Typography variant="body2">Demander Corrections</Typography>
                            </Box>
                          }
                        />
                      </RadioGroup>
                    </FormControl>
                    
                      {/* Validation Notes */}
                    <TextField
                        label="Notes de Validation"
                        placeholder="Saisissez vos commentaires et observations de validation..."
                      multiline
                      rows={4}
                        fullWidth
                      value={validationNotes}
                      onChange={(e) => setValidationNotes(e.target.value)}
                      error={!validationNotes.trim()}
                        helperText={
                          !validationNotes.trim() 
                            ? "Les notes de validation sont obligatoires pour approuver ou rejeter un échantillon" 
                            : `${validationNotes.length}/500 caractères`
                        }
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            fontSize: '0.9rem',
                            backgroundColor: 'white'
                          }
                        }}
                    />
                    </CardContent>
                  </Card>
                  </Box>
              ) : (
                <Alert severity="warning">
                  Aucune donnée d'analyse SAA disponible pour cet échantillon.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ 
          p: 2, 
          backgroundColor: '#f8f9fa',
          borderTop: '1px solid #e0e0e0',
          position: 'sticky',
          bottom: 0,
          zIndex: 1000
        }}>
          <Button 
            onClick={() => setValidationDialog(false)} 
            startIcon={<Cancel />}
            sx={{ fontSize: '0.8rem' }}
          >
            Annuler
          </Button>
          
          {/* Enable validation button for both individual samples and batch samples */}
          {(selectedSample?.metadonnees?.saa_analysis_form || 
            selectedSample?.metadonnees?.batch_analysis_summary ||
            (selectedSample as any)?.isBatchValidation) && (
            <Button 
              variant="contained" 
              onClick={handleCompleteValidation}
              startIcon={<Save />}
              disabled={!validationNotes.trim()}
              color={validationDecision === 'APPROVE' ? 'success' : 'error'}
              sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}
              title={!validationNotes.trim() ? "Veuillez saisir des notes de validation pour activer ce bouton" : ""}
            >
              {(selectedSample as any)?.isBatchValidation 
                ? (validationDecision === 'APPROVE' ? 'Approuver le Lot' : 'Rejeter le Lot')
                : (validationDecision === 'APPROVE' ? 'Approuver l\'Échantillon' : 
                   (validationDecision === 'REJECT' ? 'Rejeter l\'Échantillon' : 'Demander Corrections'))
              }
              {!validationNotes.trim() && (
                <Typography variant="caption" sx={{ ml: 1, opacity: 0.7, fontSize: '0.7rem' }}>
                  (Notes requises)
                </Typography>
              )}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default SAASupervisorDashboard; 